import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { NATIONALITIES } from "@/lib/nationalities";
import { ringStyle } from "@/lib/flagRing";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, euros, API } from "@/lib/api";
import { titleCaseName } from "@/lib/utils";
import { COUNTRIES } from "@/lib/countries";
import { SOCIAL_PLATFORMS } from "@/lib/socials";
import { PhoneInput } from "@/components/PhoneInput";
import { useAuth } from "@/context/AuthContext";
import { AuthDialog } from "@/components/AuthDialog";
import { toast } from "sonner";
import { Loader2, Trash2, Mail, Clock, Euro, Flame, Users, MessageSquare, Shield, Pencil, TrendingUp, Download, Globe, Send, ImagePlus, X, Check, Settings, UserPlus, Eye, EyeOff, KeyRound, Plus, Infinity as InfinityIcon, ChevronUp, ChevronDown, ChevronsUpDown, BadgeCheck, RefreshCw, ExternalLink, Copy, Layers, BarChart3, Lock, Unlock, ShieldCheck, Gift, Newspaper, Music, Star } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { AdminAnalytics } from "@/components/AdminAnalytics";
import { MarketingExterne } from "@/components/MarketingExterne";
import { DraggableSubTabs } from "@/components/DraggableSubTabs";
import { EmailCampaigns } from "@/components/EmailCampaigns";
import { SocialConnectionsPanel } from "@/components/SocialConnectionsPanel";
import { UtmBuilderPanel } from "@/components/UtmBuilderPanel";
import { DndContext, PointerSensor, TouchSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, rectSortingStrategy, horizontalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const LANGUAGES = [
  { code: "fr", name: "Français" }, { code: "en", name: "English" }, { code: "es", name: "Español" },
  { code: "de", name: "Deutsch" }, { code: "it", name: "Italiano" },
];
const CANDLE_PLANS = [
  { code: "decouverte", label: "Découverte (48h)" },
  { code: "1mois", label: "1 mois" },
  { code: "6mois", label: "6 mois" },
  { code: "1an", label: "1 an" },
  { code: "eternite", label: "Éternité" },
];
const CAMPAIGN_TYPES = [
  { value: "general", label: "Générale (relance)" },
  { value: "not_lit", label: "Bougie pas encore allumée" },
  { value: "expiring", label: "Bougie qui va bientôt s'éteindre" },
  { value: "extinguished", label: "Bougie éteinte à rallumer" },
  { value: "eternity", label: "Proposer l'éternité" },
];
const CAMPAIGN_PREVIEWS = {
  general: "Gardez la flamme du souvenir vivante — allumez ou ravivez une bougie en mémoire d'un être cher.",
  not_lit: "Envoyé aux clients dont la bougie n'est pas encore allumée : « Votre bougie n'est pas encore allumée — allumez-la pour qu'elle brille. »",
  expiring: "Envoyé aux clients dont la bougie va bientôt s'éteindre (72h) : « La flamme va bientôt s'éteindre — prolongez-la. »",
  extinguished: "Envoyé aux clients dont la bougie est éteinte : « La bougie s'est éteinte — rallumez-la. »",
  eternity: "Envoyé aux clients ayant une bougie non éternelle : « Faites briller cette bougie pour l'éternité. »",
};
const flagEmoji = (code) => {
  if (!code || code.length !== 2) return "🏳️";
  try { return String.fromCodePoint(...[...code.toUpperCase()].map((c) => 127397 + c.charCodeAt(0))); }
  catch { return "🏳️"; }
};
const countryName = (code) => COUNTRIES.find((c) => c.code === code)?.name || code;
const candlePhotoUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/candles/${id}/photo`;
const NET_LABELS = { facebook: "Facebook", instagram: "Instagram", twitter: "X (Twitter)", linkedin: "LinkedIn", tiktok: "TikTok", youtube: "YouTube", pinterest: "Pinterest" };
const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("fr-FR") : "—");
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return `${d.toLocaleDateString("fr-FR")} ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
};
const fmtDMY = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return d.toLocaleDateString("fr-FR");
};
const remainingLabel = (t) => {
  if (!t.paid) return "Non allumée";
  if (t.eternal) return "Éternité";
  if (t.is_expired) return "Éteinte";
  const s = t.remaining_seconds || 0;
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  if (d > 0) return `${d}j ${h}h restantes`;
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m restantes`;
};

const Stat = ({ icon: Icon, label, value, blurred }) => (
  <div className="rounded-2xl border border-white/10 bg-card/40 p-4">
    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground"><Icon size={14} className="text-sky-400" /> {label}</div>
    <div className={`mt-2 text-2xl font-medium tabular-nums transition-[filter] duration-200 ${blurred ? "select-none blur-md" : ""}`} data-testid="stat-value">{value}</div>
  </div>
);

const TAB_ACCENT = {
  sky: "border-sky-400/40 bg-sky-500/15 text-sky-200 hover:bg-sky-500/25 data-[state=active]:border-sky-400 data-[state=active]:bg-sky-500/40 data-[state=active]:text-white",
  amber: "border-amber-300/40 bg-amber-400/15 text-amber-200 hover:bg-amber-400/25 data-[state=active]:border-amber-300 data-[state=active]:bg-amber-400/40 data-[state=active]:text-white",
  fuchsia: "border-fuchsia-400/40 bg-fuchsia-500/15 text-fuchsia-200 hover:bg-fuchsia-500/25 data-[state=active]:border-fuchsia-400 data-[state=active]:bg-fuchsia-500/40 data-[state=active]:text-white",
  emerald: "border-emerald-400/40 bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25 data-[state=active]:border-emerald-400 data-[state=active]:bg-emerald-500/40 data-[state=active]:text-white",
  violet: "border-violet-400/40 bg-violet-500/15 text-violet-200 hover:bg-violet-500/25 data-[state=active]:border-violet-400 data-[state=active]:bg-violet-500/40 data-[state=active]:text-white",
  cyan: "border-cyan-400/40 bg-cyan-500/15 text-cyan-200 hover:bg-cyan-500/25 data-[state=active]:border-cyan-400 data-[state=active]:bg-cyan-500/40 data-[state=active]:text-white",
  rose: "border-rose-400/40 bg-rose-500/15 text-rose-200 hover:bg-rose-500/25 data-[state=active]:border-rose-400 data-[state=active]:bg-rose-500/40 data-[state=active]:text-white",
};
const TAB_DEFS = {
  candles: { label: "Bougies", icon: Flame, accent: "sky" },
  validate: { label: "À valider", icon: BadgeCheck, accent: "amber" },
  payments: { label: "Revenus", icon: Euro, accent: "emerald" },
  clients: { label: "Clients", icon: Users, accent: "violet" },
  marketing: { label: "Marketing", icon: Send, accent: "fuchsia" },
  settings: { label: "Réglages", icon: Settings, accent: "cyan" },
  vivant: { label: "De mon vivant", icon: Flame, accent: "rose" },
  analytics: { label: "Analyse", icon: BarChart3, accent: "emerald" },
};
const DEFAULT_TAB_ORDER = ["candles", "validate", "payments", "clients", "marketing", "settings", "vivant", "analytics"];

function SortableTab({ value, pendingCount, locked }) {
  const def = TAB_DEFS[value];
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: value, disabled: locked });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.7 : 1, zIndex: isDragging ? 50 : undefined };
  const Icon = def.icon;
  const dragProps = locked ? {} : { ...attributes, ...listeners };
  return (
    <div ref={setNodeRef} style={style} {...dragProps} className={locked ? "" : "touch-none"}>
      <TabsTrigger value={value} data-testid={`admin-tab-${value}`} className={`relative flex h-12 w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl border px-2 text-sm font-semibold transition-colors ${locked ? "" : "cursor-grab active:cursor-grabbing"} ${TAB_ACCENT[def.accent]}`}>
        <Icon size={16} /> {def.label}
        {value === "validate" && pendingCount > 0 && (
          <span className="ml-1 rounded-full bg-amber-400 px-1.5 text-xs font-bold text-black" data-testid="admin-pending-badge">{pendingCount}</span>
        )}
      </TabsTrigger>
    </div>
  );
}

function StaffTab({ value, allowed, pendingCount }) {
  const def = TAB_DEFS[value];
  const Icon = def.icon;
  if (!allowed) {
    return (
      <div
        data-testid={`admin-tab-locked-${value}`}
        title="Accès verrouillé — réservé au super admin"
        className="relative flex h-12 w-full cursor-not-allowed select-none items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-white/5 bg-white/[0.02] px-2 text-sm font-medium text-muted-foreground/40"
      >
        <Lock size={14} /> {def.label}
      </div>
    );
  }
  return (
    <TabsTrigger value={value} data-testid={`admin-tab-${value}`} className={`relative flex h-12 w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl border px-2 text-sm font-semibold transition-colors ${TAB_ACCENT[def.accent]}`}>
      <Icon size={16} /> {def.label}
      {value === "validate" && pendingCount > 0 && (
        <span className="ml-1 rounded-full bg-amber-400 px-1.5 text-xs font-bold text-black">{pendingCount}</span>
      )}
    </TabsTrigger>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  useEffect(() => {
    document.body.classList.add("admin-light");
    return () => document.body.classList.remove("admin-light");
  }, []);
  const isAdmin = user?.role === "admin";
  const isStaff = user?.role === "staff";
  const allowedTabs = isStaff ? (user?.allowed_tabs || []) : DEFAULT_TAB_ORDER;
  const [statsRevealed, setStatsRevealed] = useState(false);
  const [codeDialogOpen, setCodeDialogOpen] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [verifyingCode, setVerifyingCode] = useState(false);
  const verifySuperCode = async () => {
    setVerifyingCode(true);
    try {
      await api.post("/admin/verify-super-code", { code: codeInput });
      setStatsRevealed(true); setCodeDialogOpen(false); setCodeInput("");
    } catch { toast.error("Code super admin incorrect"); } finally { setVerifyingCode(false); }
  };
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [candles, setCandles] = useState([]);
  const [pending, setPending] = useState([]);
  const [pendingBusy, setPendingBusy] = useState(false);
  const [pendingSource, setPendingSource] = useState("all");
  const [dupOpen, setDupOpen] = useState(false);
  const [dupGroups, setDupGroups] = useState([]);
  const [dupBusy, setDupBusy] = useState(false);
  const [dupSelected, setDupSelected] = useState([]);
  const [candleSortKey, setCandleSortKey] = useState("death");
  const [candleSortDir, setCandleSortDir] = useState("desc");
  const [selectedCandleIds, setSelectedCandleIds] = useState([]);
  const [payData, setPayData] = useState({ payments: [], total_cents: 0, count: 0 });
  const [period, setPeriod] = useState("all");
  const [revGroup, setRevGroup] = useState("day");
  const [revSeries, setRevSeries] = useState([]);
  const [loadingSeries, setLoadingSeries] = useState(false);
  const [exportingRev, setExportingRev] = useState(false);
  const [settings, setSettings] = useState({ price_cents: 100, burn_hours: 48, currency: "eur" });
  const [savingSettings, setSavingSettings] = useState(false);
  const [emailCfg, setEmailCfg] = useState(null);
  const [emailPwd, setEmailPwd] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailDiag, setEmailDiag] = useState(null);
  const [emailBusy, setEmailBusy] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [hoursInput, setHoursInput] = useState({});
  const [daysInput, setDaysInput] = useState({});
  const [authOpen, setAuthOpen] = useState(false);
  // edit name/message
  const [editOpen, setEditOpen] = useState(false);
  const [editCandle, setEditCandle] = useState(null);
  const [eName, setEName] = useState("");
  const [eMsg, setEMsg] = useState("");
  const [ePhoto, setEPhoto] = useState(null);
  const [eBirth, setEBirth] = useState("");
  const [eDeath, setEDeath] = useState("");
  const [eNat, setENat] = useState("");
  const [eSocials, setESocials] = useState({});
  const [eSocialsHidden, setESocialsHidden] = useState({});
  const [discoveringSocials, setDiscoveringSocials] = useState(false);
  const [ePhotoDirty, setEPhotoDirty] = useState(false);
  const [editIsDraft, setEditIsDraft] = useState(false);
  const editFileRef = useRef();
  const [savingEdit, setSavingEdit] = useState(false);
  // messages management
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgCandle, setMsgCandle] = useState(null);
  const [msgList, setMsgList] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  // super-admin protected deletion
  const [delOpen, setDelOpen] = useState(false);
  const [delCode, setDelCode] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [delTarget, setDelTarget] = useState(null); // { type, id, label }
  // clients
  const [clientData, setClientData] = useState({ groups: [], total: 0 });
  const [clientOpen, setClientOpen] = useState(false);
  const [clientDetail, setClientDetail] = useState(null);
  const [newClientOpen, setNewClientOpen] = useState(false);
  const [ncFirst, setNcFirst] = useState("");
  const [ncLast, setNcLast] = useState("");
  const [ncEmail, setNcEmail] = useState("");
  const [ncPhone, setNcPhone] = useState("");
  const [ncCountry, setNcCountry] = useState("FR");
  const [ncLang, setNcLang] = useState("fr");
  const [ncPassword, setNcPassword] = useState("");
  const [creatingClient, setCreatingClient] = useState(false);
  const [cName, setCName] = useState("");
  const [cFirst, setCFirst] = useState("");
  const [cLast, setCLast] = useState("");
  const [cEmail, setCEmail] = useState("");
  const [cPhone, setCPhone] = useState("");
  const [cCountry, setCCountry] = useState("");
  const [cLang, setCLang] = useState("fr");
  const [cPassword, setCPassword] = useState("");
  const [savingClient, setSavingClient] = useState(false);
  // staff access management
  const [staffTabs, setStaffTabs] = useState([]);
  const [staffCode, setStaffCode] = useState("");
  const [savingStaff, setSavingStaff] = useState(false);
  // new candle (admin -> client)
  const [newCandleOpen, setNewCandleOpen] = useState(false);
  const [nkOwner, setNkOwner] = useState("");
  const [nkName, setNkName] = useState("");
  const [nkMsg, setNkMsg] = useState("");
  const [nkPhoto, setNkPhoto] = useState(null);
  const [nkPlan, setNkPlan] = useState("decouverte");
  const [nkBirth, setNkBirth] = useState("");
  const [nkDeath, setNkDeath] = useState("");
  const [nkNat, setNkNat] = useState("");
  const [creatingCandle, setCreatingCandle] = useState(false);
  const newCandleFileRef = useRef();
  // candle full detail
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailCandle, setDetailCandle] = useState(null);
  const [activeNets, setActiveNets] = useState([]);
  // campaign
  const [campType, setCampType] = useState("general");
  const [campChannel, setCampChannel] = useState("email");
  const [campTarget, setCampTarget] = useState("all");
  const [campAll, setCampAll] = useState(true);
  const [campSelected, setCampSelected] = useState([]);
  const [campSearch, setCampSearch] = useState("");
  const [sendingCamp, setSendingCamp] = useState(false);
  const [campSent, setCampSent] = useState(false);
  const [campPreview, setCampPreview] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  // export
  const [exporting, setExporting] = useState(false);
  const [allMessages, setAllMessages] = useState([]);
  const [tabOrder, setTabOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("bdc_admin_tab_order") || "null");
      if (Array.isArray(saved)) {
        const valid = saved.filter((v) => DEFAULT_TAB_ORDER.includes(v));
        const missing = DEFAULT_TAB_ORDER.filter((v) => !valid.includes(v));
        if (valid.length) return [...valid, ...missing];
      }
    } catch (e) { /* ignore */ }
    return DEFAULT_TAB_ORDER;
  });
  const [tabsLocked, setTabsLocked] = useState(() => {
    try { const v = localStorage.getItem("bdc_admin_tabs_locked"); return v === null ? true : v === "1"; } catch (e) { return true; }
  });
  const toggleTabsLock = () => setTabsLocked((v) => { const nv = !v; try { localStorage.setItem("bdc_admin_tabs_locked", nv ? "1" : "0"); } catch (e) { /* ignore */ } return nv; });
  const tabSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );
  const onTabDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setTabOrder((order) => {
      const next = arrayMove(order, order.indexOf(active.id), order.indexOf(over.id));
      try { localStorage.setItem("bdc_admin_tab_order", JSON.stringify(next)); } catch (err) { /* ignore */ }
      return next;
    });
  };

  const loadAll = useCallback(async () => {
    const calls = [
      ["stats", "/admin/stats"], ["candles", "/admin/candles"], ["settings", "/admin/settings"],
      ["clients", "/admin/clients"], ["msgs", "/admin/condolences"], ["pending", "/admin/pending-candles"],
      ["email", "/admin/email-settings"],
    ];
    const results = await Promise.allSettled(calls.map(([, url]) => api.get(url)));
    const val = (k) => { const i = calls.findIndex(([key]) => key === k); return results[i]?.status === "fulfilled" ? results[i].value.data : undefined; };
    const s = val("stats"); if (s !== undefined) setStats(s);
    const c = val("candles"); if (c !== undefined) setCandles(c);
    const set = val("settings"); if (set !== undefined) setSettings(set);
    const cl = val("clients"); if (cl !== undefined) setClientData(cl);
    const msgs = val("msgs"); if (msgs !== undefined) setAllMessages(msgs);
    const pend = val("pending"); if (pend !== undefined) setPending(pend);
    const email = val("email"); if (email !== undefined) { setEmailCfg(email); setTestTo((t) => t || email?.notify_cc || ""); }
    // Le staff n'a pas /admin/stats détaillé : garantir un objet non-null pour afficher la page
    if (s === undefined) setStats((prev) => prev || { staff: true });
  }, []);

  const loadPending = useCallback(async () => {
    try { const r = await api.get("/admin/pending-candles"); setPending(r.data); } catch (e) { /* ignore */ }
  }, []);

  const validatePending = async (id) => {
    try {
      await api.post(`/admin/pending-candles/${id}/validate`);
      toast.success("Bougie validée et publiée");
      setPending((p) => p.filter((x) => x.id !== id));
      setStats((s) => (s ? { ...s, total_candles: (s.total_candles || 0) + 1 } : s));
    } catch (e) { toast.error("Échec de la validation"); }
  };

  const rejectPending = async (id) => {
    try {
      await api.delete(`/admin/pending-candles/${id}`);
      toast.success("Brouillon supprimé");
      setPending((p) => p.filter((x) => x.id !== id));
    } catch (e) { toast.error("Échec de la suppression"); }
  };

  const pendingCountryKey = (p) => (p.nationality || p.country?.name || p.country_name || "").trim();
  const [pendingCountry, setPendingCountry] = useState("");
  const bulkDeletePending = async (ids, label) => {
    if (!ids.length) { toast.info(`Aucune proposition ${label}.`); return; }
    if (!window.confirm(`Supprimer ${ids.length} proposition(s) ${label} ? Action irréversible.`)) return;
    setPendingBusy(true);
    try {
      const r = await api.post("/admin/pending-candles/bulk-delete", { ids, blocklist: false });
      toast.success(`${r.data.deleted} proposition(s) supprimée(s)`);
      const set = new Set(ids);
      setPending((p) => p.filter((x) => !set.has(x.id)));
    } catch (e) { toast.error("Suppression impossible"); }
    finally { setPendingBusy(false); }
  };
  const deletePendingNoPhoto = () => bulkDeletePending(pending.filter((p) => !p.photo_url).map((p) => p.id), "sans photo");
  const deletePendingByCountry = () => {
    if (!pendingCountry) { toast.info("Choisissez d'abord un pays."); return; }
    bulkDeletePending(pending.filter((p) => pendingCountryKey(p) === pendingCountry).map((p) => p.id), `de « ${pendingCountry} »`);
  };

  const importNow = async () => {
    setPendingBusy(true);
    try {
      const r = await api.post("/admin/pending-candles/import");
      if (r.data.started) {
        toast.success("Import lancé sur toutes les plateformes — les fiches apparaissent au fur et à mesure.");
        setTimeout(loadPending, 8000);
        setTimeout(loadPending, 25000);
      } else {
        toast.success(`${r.data.imported} nouveau(x) décès importé(s)`);
      }
      await loadPending();
    } catch (e) { toast.error("Import impossible"); }
    finally { setPendingBusy(false); }
  };

  const detectDuplicates = async () => {
    setDupBusy(true);
    try {
      const r = await api.get("/admin/pending-candles/duplicates");
      setDupGroups(r.data);
      setDupSelected([]);
      setDupOpen(true);
      if ((r.data || []).length === 0) toast.success("Aucun doublon détecté 🎉");
    } catch (e) { toast.error("Détection impossible"); }
    finally { setDupBusy(false); }
  };

  const toggleDupSelect = (id) => {
    setDupSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  };

  const deletePendingIds = async (ids) => {
    if (!ids || ids.length === 0) return;
    try {
      const r = await api.post("/admin/pending-candles/bulk-delete", { ids, blocklist: false });
      toast.success(`${r.data.deleted} doublon(s) supprimé(s)`);
      setPending((p) => p.filter((x) => !ids.includes(x.id)));
      setDupSelected((s) => s.filter((x) => !ids.includes(x)));
      setDupGroups((groups) => groups
        .map((g) => ({ ...g, items: g.items.filter((i) => !ids.includes(i.id)), count: g.items.filter((i) => !ids.includes(i.id)).length }))
        .filter((g) => g.count > 1));
    } catch (e) { toast.error("Suppression impossible"); }
  };

  const loadPayments = useCallback(async (p) => {
    try { const res = await api.get(`/admin/payments?period=${p}`); setPayData(res.data); } catch (e) { /* accès restreint (staff) */ }
  }, []);

  const loadSeries = useCallback(async (g) => {
    setLoadingSeries(true);
    try {
      const res = await api.get(`/admin/revenue/series?group=${g}`);
      setRevSeries((res.data.series || []).map((s) => ({ ...s, euros: s.revenue_cents / 100 })));
    } catch (e) { setRevSeries([]); } finally { setLoadingSeries(false); }
  }, []);

  const exportRevenue = async () => {
    setExportingRev(true);
    try {
      const res = await api.get(`/admin/revenue/export.xlsx?period=${period}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `revenus_${period}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export de la période téléchargé");
    } catch (e) { toast.error("Export impossible"); } finally { setExportingRev(false); }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { setAuthOpen(true); return; }
    if (user.role !== "admin" && user.role !== "staff") { toast.error("Accès réservé à l'administrateur"); navigate("/"); return; }
    setAuthOpen(false);
    loadAll();
    loadPayments(period);
  }, [loading, user, navigate, loadAll, loadPayments, period]);

  useEffect(() => {
    if (user?.role === "admin" || user?.role === "staff") loadSeries(revGroup);
  }, [user, revGroup, loadSeries]);

  const loadCampPreview = useCallback(async (seg) => {
    setLoadingPreview(true);
    try {
      const res = await api.get(`/admin/campaign/preview?segment=${seg}`);
      setCampPreview(res.data.targets || []);
    } catch (e) { setCampPreview([]); } finally { setLoadingPreview(false); }
  }, []);

  useEffect(() => {
    if (user?.role === "admin" || user?.role === "staff") loadCampPreview(campType);
  }, [user, campType, loadCampPreview]);

  const loadEmailCfg = useCallback(async () => {
    try { const r = await api.get("/admin/email-settings"); setEmailCfg(r.data); setTestTo((t) => t || r.data?.notify_cc || ""); }
    catch (e) { /* ignore */ }
  }, []);

  const saveEmailCfg = async () => {
    setSavingEmail(true);
    try {
      const payload = {
        smtp_host: emailCfg.smtp_host, smtp_port: Number(emailCfg.smtp_port) || 465,
        smtp_user: emailCfg.smtp_user, smtp_from: emailCfg.smtp_from,
        notify_cc: emailCfg.notify_cc, public_app_url: emailCfg.public_app_url,
      };
      if (emailPwd) payload.smtp_password = emailPwd;
      const r = await api.put("/admin/email-settings", payload);
      setEmailCfg(r.data); setEmailPwd("");
      toast.success("Réglages e-mail enregistrés");
    } catch (e) { toast.error("Erreur d'enregistrement"); } finally { setSavingEmail(false); }
  };

  const runEmailDiag = async () => {
    setEmailBusy(true); setEmailDiag(null);
    try { const r = await api.get("/admin/email-diagnostic"); setEmailDiag(r.data); }
    catch (e) { toast.error("Diagnostic impossible"); } finally { setEmailBusy(false); }
  };

  const sendTestEmail = async () => {
    setEmailBusy(true);
    try {
      const r = await api.post("/admin/email-settings/test", { to: testTo });
      if (r.data?.mode === "smtp") toast.success(`E-mail de test envoyé à ${r.data.to}`);
      else toast.error("SMTP non configuré — e-mail NON envoyé (mode simulé)");
    } catch (e) { toast.error("Échec de l'envoi de test"); } finally { setEmailBusy(false); }
  };

  const extractVerif = (val) => {
    if (!val) return "";
    const m = String(val).match(/content=["']([^"']+)["']/i);
    return (m ? m[1] : val).trim();
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.patch("/admin/settings", {
        price_cents: Number(settings.price_cents),
        burn_hours: Number(settings.burn_hours),
        plans: settings.plans,
        plans_mode: settings.plans_mode,
        single_label: settings.single_label,
        net_fee_percent: Number(settings.net_fee_percent),
        net_fee_fixed_cents: Number(settings.net_fee_fixed_cents),
        delete_pin: settings.delete_pin,
        seo_google_verification: settings.seo_google_verification || "",
        seo_bing_verification: settings.seo_bing_verification || "",
        living_candle_enabled: !!settings.living_candle_enabled,
      });
      toast.success("Réglages enregistrés");
    } catch (e) { toast.error("Erreur"); } finally { setSavingSettings(false); }
  };

  const [interneSub, setInterneSub] = useState("offre");
  const [settingsSub, setSettingsSub] = useState("tarifs");
  const lo = settings.launch_offer || { enabled: true, price_cents: 100, hours: 720, ends_at: null, label: "", exclusive: false, candle_until: null };
  const setLO = (patch) => setSettings((s) => ({ ...s, launch_offer: { ...(s.launch_offer || { enabled: true, price_cents: 100, hours: 720, ends_at: null, label: "", exclusive: false, candle_until: null }), ...patch } }));
  const toLocalInput = (iso) => {
    if (!iso) return "";
    try { const d = new Date(iso); const off = d.getTimezoneOffset() * 60000; return new Date(d - off).toISOString().slice(0, 16); } catch { return ""; }
  };
  const saveLaunchOffer = async () => {
    setSavingSettings(true);
    try {
      const res = await api.patch("/admin/settings", { launch_offer: {
        enabled: lo.enabled !== false,
        price_cents: Number(lo.price_cents) || 100,
        hours: 720,
        ends_at: lo.ends_at || null,
        label: (lo.label || "").trim() || null,
        exclusive: !!lo.exclusive,
        candle_until: lo.candle_until || null,
      } });
      setSettings((s) => ({ ...s, launch_offer: res.data.launch_offer }));
      toast.success("Offre de lancement enregistrée");
    } catch (e) { toast.error("Erreur"); } finally { setSavingSettings(false); }
  };

  const toggleLivingCandle = async () => {
    const next = !settings.living_candle_enabled;
    try {
      await api.patch("/admin/settings", { living_candle_enabled: next });
      setSettings((s) => ({ ...s, living_candle_enabled: next }));
      toast.success(next ? "Fonctionnalité « Ma bougie de mon vivant » PUBLIÉE (visible par tous)" : "Fonctionnalité masquée (non publiée)");
    } catch { toast.error("Erreur"); }
  };

  // ===== Son du diaporama (musiques) =====
  const [dmCfg, setDmCfg] = useState(null);
  const [dmBusy, setDmBusy] = useState(false);
  const [dmProgress, setDmProgress] = useState(0);
  const dmInputRef = useRef();
  useEffect(() => { api.get("/admin/diaporama-music").then((r) => setDmCfg(r.data)).catch(() => {}); }, []);
  const dmSrc = (t) => t.custom ? `${(API || "").replace(/\/api$/, "")}${t.file}` : `${t.file}`;
  const dmPatch = async (patch) => {
    try { const r = await api.patch("/admin/diaporama-music", patch); setDmCfg(r.data); }
    catch { toast.error("Erreur"); }
  };
  const dmToggleBuiltin = (id, enabled) => {
    if (!dmCfg) return;
    const disabled = dmCfg.builtins.filter((b) => (b.id === id ? !enabled : !b.enabled)).map((b) => b.id);
    dmPatch({ disabled_ids: disabled });
  };
  const dmSetDefault = (id) => dmPatch({ default_choice: id });
  const uploadDmMusic = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error("Musique trop lourde (max 20 Mo)"); return; }
    const label = window.prompt("Nom de cette musique (affiché aux utilisateurs) ?", (file.name || "").replace(/\.[^.]+$/, "")) || "";
    setDmBusy(true); setDmProgress(0);
    try {
      const { data: init } = await api.post("/admin/diaporama-music/init");
      const CHUNK = 1024 * 1024; const total = Math.ceil(file.size / CHUNK);
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", init.upload_id); fd.append("index", String(i));
        fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), "chunk");
        await api.post("/admin/diaporama-music/chunk", fd);
        setDmProgress(Math.round(((i + 1) / total) * 100));
      }
      const fd2 = new FormData();
      fd2.append("upload_id", init.upload_id); fd2.append("filename", file.name || "musique.mp3");
      fd2.append("content_type", file.type || "audio/mpeg"); fd2.append("label", label);
      const { data } = await api.post("/admin/diaporama-music/complete", fd2);
      setDmCfg(data); toast.success("Musique ajoutée ✓");
    } catch (err) { toast.error(err.response?.data?.detail || "Envoi impossible"); }
    finally { setDmBusy(false); setDmProgress(0); }
  };
  const renameDmCustom = async (c) => {
    const label = window.prompt("Nouveau nom de cette musique :", c.label || "");
    if (label === null) return;
    if (!label.trim()) { toast.error("Le nom ne peut pas être vide"); return; }
    try { const r = await api.patch(`/admin/diaporama-music/custom/${c.id}`, { label: label.trim() }); setDmCfg(r.data); toast.success("Nom mis à jour"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const deleteDmCustom = async (id) => {
    if (!window.confirm("Supprimer cette musique de la liste proposée ?")) return;
    try { const r = await api.delete(`/admin/diaporama-music/${id}`); setDmCfg(r.data); toast.success("Supprimée"); }
    catch { toast.error("Erreur"); }
  };


  const [livingTestEmail, setLivingTestEmail] = useState("");
  const saveLivingTestEmails = async (list) => {
    try {
      const r = await api.patch("/admin/settings", { living_candle_test_emails: list });
      setSettings((s) => ({ ...s, living_candle_test_emails: r.data.living_candle_test_emails || list }));
    } catch { toast.error("Erreur"); }
  };
  const addLivingTestEmail = async () => {
    const e = (livingTestEmail || "").trim().toLowerCase();
    if (!e || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { toast.error("E-mail invalide"); return; }
    const cur = settings.living_candle_test_emails || [];
    if (cur.includes(e)) { toast.error("Déjà dans la liste"); return; }
    await saveLivingTestEmails([...cur, e]);
    setLivingTestEmail("");
    toast.success("Profil de test ajouté");
  };
  const removeLivingTestEmail = async (e) => {
    await saveLivingTestEmails((settings.living_candle_test_emails || []).filter((x) => x !== e));
    toast.success("Profil de test retiré");
  };

  const [livingP2, setLivingP2] = useState(null);
  const [livingList, setLivingList] = useState([]);
  const loadLiving = async () => {
    try { const r = await api.get("/admin/living-candles"); setLivingList(r.data.items || []); } catch { /* ignore */ }
  };
  const [lcDetail, setLcDetail] = useState(null);
  const [lcDetailOpen, setLcDetailOpen] = useState(false);
  const [lcDetailBusy, setLcDetailBusy] = useState(false);
  const openLivingDetail = async (lc) => {
    setLcDetailOpen(true); setLcDetail(null); setLcDetailBusy(true);
    try { const r = await api.get(`/admin/living-candles/${lc.id}/detail`); setLcDetail(r.data); }
    catch { toast.error("Chargement du détail impossible"); }
    finally { setLcDetailBusy(false); }
  };
  useEffect(() => { loadLiving(); }, []);
  const [pubOpen, setPubOpen] = useState(false);
  const [pubLc, setPubLc] = useState(null);
  const [pubDeath, setPubDeath] = useState("");
  const [pubBusy, setPubBusy] = useState(false);
  const openPublish = (lc) => { setPubLc(lc); setPubDeath(lc.death_date || new Date().toISOString().slice(0, 10)); setPubOpen(true); };
  const doPublish = async () => {
    if (!pubLc) return;
    setPubBusy(true);
    try {
      await api.post(`/admin/living-candles/${pubLc.id}/publish`, { death_date: pubDeath || null });
      toast.success("Bougie publiée — elle apparaît dans les commémoratives");
      setPubOpen(false);
      await loadLiving();
    } catch (e) { toast.error(e.response?.data?.detail || "Publication impossible"); }
    finally { setPubBusy(false); }
  };
  const doUnpublish = async (lc) => {
    if (!window.confirm("Dépublier cette bougie ? Elle sera retirée des bougies publiques.")) return;
    try {
      await api.post(`/admin/living-candles/${lc.id}/unpublish`);
      toast.success("Bougie dépubliée");
      await loadLiving();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const sendLivingMessages = async (lc) => {
    if (!window.confirm(`Contrôle effectué ? Publier l'hommage de « ${lc.display_name || lc.owner_email} » et envoyer les ${lc.messages_count} message(s) enregistré(s) à leurs destinataires ?`)) return;
    try {
      const r = await api.post(`/admin/living-candles/${lc.id}/send-messages`);
      toast.success(`Hommage publié — ${r.data.sent} message(s) envoyé(s)${r.data.failed ? `, ${r.data.failed} échec(s)` : ""}`);
      await loadLiving();
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible"); }
  };
  const [blogGen, setBlogGen] = useState(null);
  const generateBlog = async (c) => {
    if (c.has_blog && c.blog_slug) { window.open(`/blog/${c.blog_slug}`, "_blank"); return; }
    setBlogGen(c.id);
    try {
      const r = await api.post(`/admin/candles/${c.id}/blog`);
      toast.success("Article généré et publié ✅");
      setCandles((list) => list.map((x) => x.id === c.id ? { ...x, has_blog: true, blog_slug: r.data.slug } : x));
    } catch (e) { toast.error(e.response?.data?.detail || "Génération impossible"); }
    finally { setBlogGen(null); }
  };
  useEffect(() => {
    if (settings && livingP2 === null && settings.living_ceremony_price_cents !== undefined) {
      const cp = settings.living_ceremony_promo || {};
      const mp = settings.living_message_promo || {};
      setLivingP2({
        ceremony_eur: (settings.living_ceremony_price_cents || 0) / 100,
        message_eur: (settings.living_message_price_cents || 0) / 100,
        access_type: settings.living_access_type || "download",
        access_days: settings.living_access_days || 0,
        ceremony_payer: settings.living_ceremony_payer || "both",
        cer_promo_active: !!cp.active, cer_promo_type: cp.type || "price", cer_promo_eur: (cp.price_cents || 0) / 100, cer_promo_percent: cp.percent || 0, cer_promo_start: cp.start || "", cer_promo_end: cp.end || "",
        msg_promo_active: !!mp.active, msg_promo_type: mp.type || "price", msg_promo_eur: (mp.price_cents || 0) / 100, msg_promo_percent: mp.percent || 0, msg_promo_start: mp.start || "", msg_promo_end: mp.end || "",
        bundles: (settings.living_message_bundles || []).map((b) => ({ ...b, price_eur: (b.price_cents || 0) / 100 })),
      });
    }
  }, [settings, livingP2]);
  const saveLivingP2 = async () => {
    try {
      const r = await api.patch("/admin/settings", {
        living_ceremony_price_cents: Math.round((livingP2.ceremony_eur || 0) * 100),
        living_message_price_cents: Math.round((livingP2.message_eur || 0) * 100),
        living_access_type: livingP2.access_type,
        living_access_days: parseInt(livingP2.access_days || 0, 10),
        living_ceremony_payer: livingP2.ceremony_payer,
        living_ceremony_promo: { active: !!livingP2.cer_promo_active, type: livingP2.cer_promo_type, price_cents: Math.round((livingP2.cer_promo_eur || 0) * 100), percent: parseInt(livingP2.cer_promo_percent || 0, 10), start: livingP2.cer_promo_start || null, end: livingP2.cer_promo_end || null },
        living_message_promo: { active: !!livingP2.msg_promo_active, type: livingP2.msg_promo_type, price_cents: Math.round((livingP2.msg_promo_eur || 0) * 100), percent: parseInt(livingP2.msg_promo_percent || 0, 10), start: livingP2.msg_promo_start || null, end: livingP2.msg_promo_end || null },
        living_message_bundles: (livingP2.bundles || []).map((b) => ({ id: b.id, label: b.label, qty: parseInt(b.qty || 0, 10), price_cents: Math.round((b.price_eur || 0) * 100), bonus: parseInt(b.bonus || 0, 10) })),
      });
      setSettings((s) => ({ ...s, ...r.data }));
      const cp = r.data.living_ceremony_promo || {}; const mp = r.data.living_message_promo || {};
      setLivingP2((p) => ({ ...p, bundles: (r.data.living_message_bundles || []).map((b) => ({ ...b, price_eur: (b.price_cents || 0) / 100 })), cer_promo_eur: (cp.price_cents || 0) / 100, msg_promo_eur: (mp.price_cents || 0) / 100 }));
      toast.success("Tarifs « De mon vivant » enregistrés");
    } catch { toast.error("Erreur"); }
  };

  const updatePlanPrice = (code, euroVal) => {    setSettings((s) => ({ ...s, plans: (s.plans || []).map((p) => p.code === code ? { ...p, price_cents: Math.round(Number(euroVal || 0) * 100) } : p) }));
  };

  const changePlan = async (id, plan) => {
    try {
      await api.patch(`/admin/candles/${id}`, { plan });
      toast.success("Forfait appliqué");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const setRemaining = async (id) => {
    const dRaw = daysInput[id]; const hRaw = hoursInput[id];
    if ((dRaw === undefined || dRaw === "") && (hRaw === undefined || hRaw === "")) {
      return toast.error("Saisissez des jours et/ou des heures");
    }
    const total = Number(dRaw || 0) * 24 + Number(hRaw || 0);
    try {
      await api.patch(`/admin/candles/${id}`, { set_remaining_hours: total });
      toast.success("Temps restant ajusté");
      setDaysInput((s) => ({ ...s, [id]: "" }));
      setHoursInput((s) => ({ ...s, [id]: "" }));
      loadAll();
    } catch (e) { toast.error("Erreur"); }
  };

  const remind = async (id) => {
    try { const r = await api.post(`/admin/candles/${id}/remind`); toast.success(`Relance envoyée (${r.data.mode})`); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const openEdit = (c) => {
    setEditCandle(c);
    setEName(c.deceased_name || "");
    setEMsg(c.tribute_message || "");
    setEPhoto(c.photo_base64 || null);
    setEBirth(c.birth_date || "");
    setEDeath(c.death_date || "");
    setENat(c.nationality || "");
    setESocials(c.socials || {});
    setESocialsHidden(Object.fromEntries((c.socials_hidden || []).map((k) => [k, true])));
    setEPhotoDirty(false);
    setEditIsDraft(false);
    setEditOpen(true);
  };

  const openEditDraft = (p) => {
    setEditCandle({ id: p.id, status: "draft" });
    setEName(p.deceased_name || "");
    setEMsg(p.tribute_message || "");
    setEPhoto(p.photo_base64 || (p.photo_url ? `${process.env.REACT_APP_BACKEND_URL}${p.photo_url}` : null));
    setEBirth(p.birth_date || "");
    setEDeath(p.death_date || "");
    setENat(p.nationality || "");
    setESocials(p.socials || {});
    setESocialsHidden(Object.fromEntries((p.socials_hidden || []).map((k) => [k, true])));
    setEPhotoDirty(false);
    setEditIsDraft(true);
    setEditOpen(true);
  };

  const discoverSocials = async () => {
    if (!editCandle?.id) return;
    setDiscoveringSocials(true);
    try {
      const r = await api.post(`/admin/candles/${editCandle.id}/discover-socials`);
      const found = r.data?.socials || {};
      setESocials(found);
      setESocialsHidden({});
      const n = Object.keys(found).length;
      toast.success(n > 0 ? `${n} lien(s) trouvé(s) — vérifiez-les` : "Aucun réseau trouvé");
    } catch (e) { toast.error("Échec de la détection"); } finally { setDiscoveringSocials(false); }
  };

  const onEditPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("Photo trop lourde (max 3 Mo)");
    const reader = new FileReader();
    reader.onload = () => { setEPhoto(reader.result); setEPhotoDirty(true); };
    reader.readAsDataURL(file);
  };

  const saveEdit = async (validateAfter = false) => {
    if (!eName.trim()) return toast.error("Le nom est requis");
    setSavingEdit(true);
    try {
      const payload = {
        deceased_name: eName,
        tribute_message: eMsg,
        birth_date: eBirth || null,
        death_date: eDeath || null,
        nationality: eNat || null,
        socials: eSocials || {},
        socials_hidden: Object.keys(eSocialsHidden).filter((k) => eSocialsHidden[k]),
      };
      if (ePhotoDirty) payload.photo_base64 = ePhoto ?? "";
      const res = await api.patch(`/admin/candles/${editCandle.id}`, payload);
      if (validateAfter) {
        await api.post(`/admin/pending-candles/${editCandle.id}/validate`);
        toast.success("Bougie complétée, validée et publiée");
        setPending((p) => p.filter((x) => x.id !== editCandle.id));
        setStats((s) => (s ? { ...s, total_candles: (s.total_candles || 0) + 1 } : s));
      } else {
        toast.success("Bougie mise à jour");
      }
      setEditOpen(false);
      setCandles((list) => list.map((c) => (c.id === editCandle.id ? { ...c, ...(res?.data || {}) } : c)));
    } catch (e) { toast.error("Erreur"); } finally { setSavingEdit(false); }
  };

  const openMessages = async (c) => {
    setMsgCandle(c);
    setMsgOpen(true);
    setLoadingMsgs(true);
    try {
      const r = await api.get(`/candles/${c.id}/condolences`);
      setMsgList(r.data);
    } catch (e) { setMsgList([]); } finally { setLoadingMsgs(false); }
  };

  const deleteCondolence = (condId) => {
    askDelete("condolence", condId, "ce message");
  };

  const askDelete = (type, id, label) => {
    setDelTarget({ type, id, label });
    setDelCode("");
    setDelOpen(true);
  };

  const confirmDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    const { type, id } = delTarget;
    const q = `?code=${encodeURIComponent(delCode)}`;
    try {
      if (type === "candle") {
        await api.delete(`/admin/candles/${id}${q}`);
      } else if (type === "bulk") {
        await api.post(`/admin/candles/bulk-delete`, { ids: selectedCandleIds, code: delCode });
        setSelectedCandleIds([]);
      } else if (type === "condolence") {
        await api.delete(`/admin/condolences/${id}${q}`);
        setMsgList((l) => l.filter((m) => m.id !== id));
      } else if (type === "user") {
        await api.delete(`/admin/users/${id}${q}`);
        setClientOpen(false);
      } else if (type === "living") {
        await api.delete(`/admin/living-candles/${id}${q}`);
        setLivingList((l) => l.filter((x) => x.id !== id));
        toast.success("Suppression effectuée");
        setDelOpen(false);
        setDeleting(false);
        return;
      } else if (type === "cleanup-empty") {
        const r = await api.delete(`/admin/clients/cleanup-empty${q}`);
        toast.success(`${r.data.deleted} client(s) sans bougie supprimé(s)`);
        setDelOpen(false);
        setDeleting(false);
        loadAll();
        return;
      }
      toast.success("Suppression effectuée");
      setDelOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Code super admin incorrect");
    } finally { setDeleting(false); }
  };

  const openDelete = (c) => askDelete("candle", c.id, `« ${c.deceased_name} »`);

  const deleteCondolencePhoto = async (condId, idx) => {
    try {
      const r = await api.delete(`/admin/condolences/${condId}/photo/${idx}`);
      const stamp = Date.now();
      const newUrls = Array.from({ length: r.data.photo_count || 0 }, (_, i) => `/api/condolences/${condId}/photo/${i}?v=${stamp}`);
      setAllMessages((list) => list.map((m) => (m.id === condId ? { ...m, photo_urls: newUrls } : m)));
      toast.success("Photo supprimée");
    } catch (e) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const toggleCandleSort = (key) => {
    if (candleSortKey === key) setCandleSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setCandleSortKey(key); setCandleSortDir(key === "created_at" ? "desc" : "asc"); }
  };

  const sortedCandles = useMemo(() => {
    const dir = candleSortDir === "asc" ? 1 : -1;
    const val = (c) => {
      switch (candleSortKey) {
        case "name": return (c.deceased_name || "").toLowerCase();
        case "death": return (c.death_date || "").slice(0, 10);
        case "state": return c.paid ? (c.remaining_seconds || 0) : -1;
        case "raised": return c.total_paid_cents || 0;
        default: return c.created_at || "";
      }
    };
    return [...candles].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [candles, candleSortKey, candleSortDir]);

  const allCandlesSelected = sortedCandles.length > 0 && selectedCandleIds.length === sortedCandles.length;
  const toggleSelectAllCandles = () => setSelectedCandleIds(allCandlesSelected ? [] : sortedCandles.map((c) => c.id));
  const toggleSelectCandle = (id) => setSelectedCandleIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const openBulkDelete = () => askDelete("bulk", null, `${selectedCandleIds.length} bougie(s)`);

  const SortHead = ({ k, label, className }) => (
    <TableHead className={className}>
      <button type="button" onClick={() => toggleCandleSort(k)} className="inline-flex items-center gap-1 hover:text-foreground" data-testid={`admin-sort-${k}`}>
        {label}
        {candleSortKey === k ? (candleSortDir === "asc" ? <ChevronUp size={13} /> : <ChevronDown size={13} />) : <ChevronsUpDown size={13} className="opacity-40" />}
      </button>
    </TableHead>
  );

  const openClient = async (clientId) => {
    setClientOpen(true);
    setClientDetail(null);
    try {
      const r = await api.get(`/admin/clients/${clientId}`);
      setClientDetail(r.data);
      const cl = r.data.client;
      setCName(cl.name || ""); setCFirst(cl.first_name || ""); setCLast(cl.last_name || ""); setCEmail(cl.email || ""); setCPhone(cl.phone || "");
      setCCountry(cl.country || "FR"); setCLang(cl.language || "fr"); setCPassword("");
      setStaffTabs(cl.allowed_tabs || []); setStaffCode("");
    } catch (e) { toast.error("Client introuvable"); setClientOpen(false); }
  };

  const openNewCandle = () => {
    setNkOwner(""); setNkName(""); setNkMsg(""); setNkPhoto(null); setNkPlan("decouverte"); setNkNat("");
    setNewCandleOpen(true);
  };

  const onNewCandlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("Photo trop lourde (max 3 Mo)");
    const reader = new FileReader();
    reader.onload = () => setNkPhoto(reader.result);
    reader.readAsDataURL(file);
  };

  const createCandle = async () => {
    if (!nkOwner) return toast.error("Sélectionnez un client");
    if (!nkName.trim()) return toast.error("Le nom du défunt est requis");
    setCreatingCandle(true);
    try {
      await api.post("/admin/candles", {
        owner_id: nkOwner, deceased_name: nkName, tribute_message: nkMsg, photo_base64: nkPhoto ?? "", plan: nkPlan, birth_date: nkBirth || null, death_date: nkDeath || null, nationality: nkNat || null,
      });
      toast.success("Bougie créée et allumée");
      setNewCandleOpen(false);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setCreatingCandle(false); }
  };

  const openDetail = (c) => { setDetailCandle(c); setDetailOpen(true); };

  const toggleBroadcast = async (net) => {
    if (!detailCandle) return;
    const cur = detailCandle.broadcast_networks || [];
    const next = cur.includes(net) ? cur.filter((n) => n !== net) : [...cur, net];
    setDetailCandle((d) => ({ ...d, broadcast_networks: next }));
    setCandles((list) => list.map((x) => x.id === detailCandle.id ? { ...x, broadcast_networks: next } : x));
    try { await api.patch(`/admin/candles/${detailCandle.id}`, { broadcast_networks: next }); }
    catch { toast.error("Erreur d'enregistrement"); }
  };

  useEffect(() => {
    api.get("/social-networks/active").then((r) => setActiveNets(r.data?.networks || [])).catch(() => setActiveNets([]));
  }, []);

  const openNewClient = () => {
    setNcFirst(""); setNcLast(""); setNcEmail(""); setNcPhone(""); setNcCountry("FR"); setNcLang("fr"); setNcPassword("");
    setNewClientOpen(true);
  };

  const createClient = async () => {
    if (!ncEmail.trim()) { toast.error("L'email est obligatoire"); return; }
    setCreatingClient(true);
    try {
      await api.post("/admin/clients", {
        first_name: ncFirst, last_name: ncLast, email: ncEmail.trim(), phone: ncPhone,
        country: ncCountry, language: ncLang, password: ncPassword || null,
      });
      toast.success("Client créé");
      setNewClientOpen(false);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setCreatingClient(false); }
  };

  const saveClient = async () => {
    if (!clientDetail) return;
    setSavingClient(true);
    try {
      await api.patch(`/admin/clients/${clientDetail.client.id}`, {
        first_name: cFirst, last_name: cLast, email: cEmail, phone: cPhone, country: cCountry, language: cLang,
        ...(cPassword.trim() ? { password: cPassword.trim() } : {}),
      });
      toast.success(cPassword.trim() ? "Fiche client et mot de passe mis à jour" : "Fiche client mise à jour");
      setClientOpen(false);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setSavingClient(false); }
  };

  const toggleStaffTab = (t) => setStaffTabs((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);
  const saveStaff = async () => {
    if (!clientDetail) return;
    if (!staffCode.trim()) { toast.error("Saisissez le code super admin"); return; }
    setSavingStaff(true);
    try {
      const r = await api.put(`/admin/clients/${clientDetail.client.id}/staff`, { allowed_tabs: staffTabs, code: staffCode.trim() });
      setClientDetail((d) => ({ ...d, client: { ...d.client, role: r.data.role, allowed_tabs: r.data.allowed_tabs } }));
      setStaffCode("");
      toast.success(r.data.role === "staff" ? "Accès staff enregistrés" : "Rôle staff retiré");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Code super admin incorrect"); } finally { setSavingStaff(false); }
  };

  const toggleCampClient = (id) => {
    setCampSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const sendCampaign = async () => {
    let payload;
    if (campType === "general") {
      if (!campAll && campSelected.length === 0) { toast.error("Sélectionnez au moins un client"); return; }
      payload = campAll ? { channel: campChannel, target: "all" } : { channel: campChannel, target_ids: campSelected };
    } else {
      payload = { channel: campChannel, segment: campType };
    }
    setSendingCamp(true);
    try {
      const r = await api.post("/admin/campaign", payload);
      if (r.data.recipients === 0) {
        toast.info("Aucun client concerné pour cette campagne.");
      } else {
        toast.success(`Campagne envoyée : ${r.data.emails_sent} e-mail(s), ${r.data.notifications_created} notification(s)`);
        setCampSent(true);
        setTimeout(() => setCampSent(false), 4000);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setSendingCamp(false); }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/export.xlsx", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `bougieducoeur_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
    } catch (e) { toast.error("Export impossible"); } finally { setExporting(false); }
  };

  const allClientsFlat = clientData.groups.flatMap((g) => g.clients);

  if (!loading && !user) {
    return (
      <div className="min-h-[100dvh]">
        <Header />
        <div className="flex flex-col items-center justify-center gap-4 px-6 py-24 text-center">
          <Shield className="text-sky-400" size={40} />
          <h1 className="text-2xl font-medium">Espace administrateur</h1>
          <p className="max-w-sm text-sm text-muted-foreground">Connectez-vous avec votre compte administrateur pour accéder au tableau de bord.</p>
          <Button className="rounded-full" onClick={() => setAuthOpen(true)} data-testid="admin-login-open">Se connecter</Button>
        </div>
        <AuthDialog open={authOpen} onOpenChange={setAuthOpen} onSuccess={() => {}} />
      </div>
    );
  }

  if (!stats) return <div className="flex h-[100dvh] items-center justify-center"><Loader2 className="animate-spin text-sky-400" /></div>;

  return (
    <div className="min-h-[100dvh]">
      <Header />
      <div className="mx-auto max-w-5xl px-5 py-8 pb-28">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-medium">{isStaff ? "Espace staff" : "Tableau de bord administrateur"}</h1>
            {!isStaff && (
              <button
                type="button"
                onClick={() => (statsRevealed ? setStatsRevealed(false) : setCodeDialogOpen(true))}
                className="rounded-full border border-white/10 bg-white/5 p-2 text-muted-foreground transition-colors hover:text-white"
                title={statsRevealed ? "Masquer les chiffres" : "Afficher les chiffres (super admin)"}
                data-testid="stats-reveal-toggle"
              >
                {statsRevealed ? <Eye size={16} /> : <EyeOff size={16} />}
              </button>
            )}
          </div>
          {!isStaff && (
            <Button variant="outline" className="rounded-full border-white/15" onClick={exportExcel} disabled={exporting} data-testid="export-excel-btn">
              {exporting ? <Loader2 className="animate-spin" size={16} /> : <><Download size={15} className="mr-2" /> Export Excel</>}
            </Button>
          )}
        </div>

        {isStaff ? (
          <div className="mb-8 rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-sm text-muted-foreground" data-testid="staff-banner">
            Vous êtes connecté en tant que <span className="text-foreground">membre du staff</span>. Seuls vos onglets autorisés sont accessibles.
          </div>
        ) : (
          <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            <Stat icon={Euro} label="Revenus bruts" value={euros(stats.total_revenue_cents)} blurred={!statsRevealed} />
            <Stat icon={TrendingUp} label="Revenus nets" value={euros(stats.net_revenue_cents ?? 0)} blurred={!statsRevealed} />
            <Stat icon={Flame} label="Bougies actives" value={stats.active_candles} blurred={!statsRevealed} />
            <Stat icon={Flame} label="Total bougies" value={stats.total_candles} blurred={!statsRevealed} />
            <Stat icon={MessageSquare} label="Messages" value={stats.total_condolences} blurred={!statsRevealed} />
            <Stat icon={Users} label="Utilisateurs" value={stats.total_users} blurred={!statsRevealed} />
          </div>
        )}

        <Dialog open={codeDialogOpen} onOpenChange={setCodeDialogOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Accès réservé au super admin</DialogTitle>
              <DialogDescription>Saisissez le code super admin pour afficher les chiffres du tableau de bord.</DialogDescription>
            </DialogHeader>
            <Input
              type="password"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") verifySuperCode(); }}
              placeholder="Code super admin"
              autoFocus
              data-testid="super-code-input"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="rounded-full border-white/15" onClick={() => setCodeDialogOpen(false)}>Annuler</Button>
              <Button className="rounded-full" onClick={verifySuperCode} disabled={verifyingCode} data-testid="super-code-submit">
                {verifyingCode ? <Loader2 className="animate-spin" size={16} /> : "Afficher"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Tabs defaultValue={isStaff ? (allowedTabs.includes("marketing") ? "campagnes" : (allowedTabs[0] || "candles")) : "campagnes"}>
          {!isStaff && (
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground" data-testid="admin-tabs-hint">
                {tabsLocked ? "Onglets verrouillés — ils ne bougeront plus au scroll." : "Astuce : glissez-déposez les onglets pour les ranger dans l'ordre que vous voulez."}
              </p>
              <button type="button" onClick={toggleTabsLock} data-testid="admin-tabs-lock-btn"
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${tabsLocked ? "border-emerald-400/40 bg-emerald-500/10 text-emerald-300" : "border-amber-300/40 bg-amber-300/10 text-amber-200"}`}>
                {tabsLocked ? <><Lock size={13} /> Verrouillé</> : <><Unlock size={13} /> Déverrouillé</>}
              </button>
            </div>
          )}
          {(!isStaff || allowedTabs.includes("marketing")) && (
            <TabsList className="mb-4 flex h-auto w-full bg-transparent p-0">
              <TabsTrigger value="campagnes" data-testid="admin-tab-campagnes" className="flex h-16 w-full items-center justify-center gap-3 rounded-2xl border-2 border-fuchsia-400/60 bg-gradient-to-r from-fuchsia-600/30 via-fuchsia-500/20 to-sky-500/20 text-lg font-bold text-fuchsia-100 shadow-[0_0_30px_rgba(217,70,239,0.25)] transition-colors hover:from-fuchsia-600/45 data-[state=active]:border-fuchsia-300 data-[state=active]:from-fuchsia-600/60 data-[state=active]:text-white">
                <Mail size={22} /> Campagnes e-mail
                <span className="rounded-full bg-fuchsia-400 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-black">Prioritaire</span>
              </TabsTrigger>
            </TabsList>
          )}
          <TabsList className="grid h-auto w-full grid-cols-2 gap-2 bg-transparent p-0 sm:grid-cols-3 md:grid-cols-4">
            {isStaff ? (
              DEFAULT_TAB_ORDER.map((v) => (
                <StaffTab key={v} value={v} allowed={allowedTabs.includes(v)} pendingCount={pending.length} />
              ))
            ) : (
              <DndContext sensors={tabSensors} collisionDetection={closestCenter} onDragEnd={onTabDragEnd}>
                <SortableContext items={tabOrder} strategy={rectSortingStrategy}>
                  {tabOrder.map((v) => (
                    <SortableTab key={v} value={v} pendingCount={pending.length} locked={tabsLocked} />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </TabsList>

          <TabsContent value="campagnes" className="mt-4">
            <EmailCampaigns />
          </TabsContent>

          <TabsContent value="validate" className="mt-4">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-foreground">Bougies à valider</h3>
                <p className="text-sm text-muted-foreground">Décès importés automatiquement (JeSuisMort.com · Wikipédia · Anniversaire-Célébrité) — vérifiez avant publication.</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="outline" className="rounded-full border-amber-300/40 text-amber-200 hover:bg-amber-300/10" onClick={detectDuplicates} disabled={dupBusy} data-testid="admin-detect-duplicates-btn">
                  {dupBusy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Copy size={15} className="mr-1.5" />} Détecter les doublons
                </Button>
                <Button size="sm" variant="outline" className="rounded-full border-white/15" onClick={importNow} disabled={pendingBusy} data-testid="admin-import-now-btn">
                  {pendingBusy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <RefreshCw size={15} className="mr-1.5" />} Importer maintenant
                </Button>
              </div>
            </div>
            {/* Suppression groupée : sans photo / par pays */}
            <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.03] p-3" data-testid="admin-pending-bulk-tools">
              <Button size="sm" variant="destructive" className="rounded-full" onClick={deletePendingNoPhoto} disabled={pendingBusy} data-testid="admin-delete-nophoto-btn">
                <Trash2 size={14} className="mr-1.5" /> Supprimer sans photo ({pending.filter((p) => !p.photo_url).length})
              </Button>
              <div className="flex items-center gap-2">
                <select
                  value={pendingCountry}
                  onChange={(e) => setPendingCountry(e.target.value)}
                  className="h-9 rounded-full border border-white/15 bg-transparent px-3 text-sm text-foreground outline-none"
                  data-testid="admin-pending-country-select"
                >
                  <option value="" className="bg-[#141110]">— Choisir un pays —</option>
                  {Object.entries(pending.reduce((acc, p) => { const k = pendingCountryKey(p); if (k) acc[k] = (acc[k] || 0) + 1; return acc; }, {}))
                    .sort((a, b) => b[1] - a[1])
                    .map(([name, n]) => (<option key={name} value={name} className="bg-[#141110]">{name} ({n})</option>))}
                </select>
                <Button size="sm" variant="destructive" className="rounded-full" onClick={deletePendingByCountry} disabled={pendingBusy || !pendingCountry} data-testid="admin-delete-country-btn">
                  <Trash2 size={14} className="mr-1.5" /> Supprimer ce pays
                </Button>
              </div>
            </div>
            {(() => {
              const counts = pending.reduce((acc, p) => { const s = p.source || "autre"; acc[s] = (acc[s] || 0) + 1; return acc; }, {});
              const labels = { jesuismort: "JeSuisMort", wikipedia: "Wikipédia FR", wikipedia_en: "Wikipédia Int'l", anniversaire: "Anniversaire-Célébrité", wikidata: "Wikidata", presse: "Presse", autre: "Autre" };
              const order = ["jesuismort", "wikipedia", "wikipedia_en", "anniversaire", "wikidata", "presse"];
              if (counts["autre"]) order.push("autre");
              return (
                <div className="mb-4 flex flex-wrap gap-2" data-testid="admin-pending-source-tabs">
                  <button onClick={() => setPendingSource("all")} className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${pendingSource === "all" ? "border-amber-300/60 bg-amber-300/15 text-amber-100" : "border-white/10 bg-white/5 text-muted-foreground hover:border-white/20"}`} data-testid="admin-pending-tab-all">
                    Toutes ({pending.length})
                  </button>
                  {order.map((s) => (
                    <button key={s} onClick={() => setPendingSource(s)} className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${pendingSource === s ? "border-amber-300/60 bg-amber-300/15 text-amber-100" : "border-white/10 bg-white/5 text-muted-foreground hover:border-white/20"}`} data-testid={`admin-pending-tab-${s}`}>
                      {labels[s]} ({counts[s] || 0})
                    </button>
                  ))}
                </div>
              );
            })()}
            {(() => {
              const filtered = pending.filter((p) => pendingSource === "all" || (p.source || "autre") === pendingSource);
              if (filtered.length === 0) {
                return (
                  <div className="rounded-2xl border border-white/10 bg-white/5 py-12 text-center text-sm text-muted-foreground" data-testid="admin-pending-empty">
                    Aucune bougie en attente de validation.
                  </div>
                );
              }
              return (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {pending.filter((p) => pendingSource === "all" || (p.source || "autre") === pendingSource).map((p) => (
                  <div key={p.id} className="flex flex-col rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="admin-pending-card">
                    <div className="flex items-start gap-3">
                      {p.photo_url ? (
                        <div className={`shrink-0 rounded-full ${ringStyle(p.flag_colors) ? "p-[2px]" : ""}`} style={ringStyle(p.flag_colors) || undefined} title={p.country_name || ""}>
                          <img src={`${process.env.REACT_APP_BACKEND_URL}${p.photo_url}`} alt={p.deceased_name} className={`h-16 w-16 rounded-full object-cover ${ringStyle(p.flag_colors) ? "border border-black/50" : "border-2 border-amber-300/60"}`} />
                        </div>
                      ) : (
                        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border-2 border-white/15 bg-white/5"><Flame size={22} className="text-amber-300/60" /></div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold text-foreground" data-testid="admin-pending-name">{p.deceased_name}</p>
                        {p.source_label && (
                          <span className="mt-0.5 inline-block rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-medium text-muted-foreground" data-testid="admin-pending-source-label">{p.source_label}</span>
                        )}
                        {(p.birth_date || p.death_date) ? (
                          <p className="text-sm text-muted-foreground" data-testid="admin-pending-dates">{fmtDMY(p.birth_date) || p.source_years?.birth || "?"} – {fmtDMY(p.death_date) || p.source_years?.death || "?"}</p>
                        ) : (p.source_years?.birth && p.source_years?.death && (
                          <p className="text-sm text-muted-foreground">{p.source_years.birth} – {p.source_years.death}</p>
                        ))}
                        {(p.country || p.nationality) && (
                          <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="admin-pending-nationality">
                            {p.country?.flag && <img src={p.country.flag} alt="" className="h-3 w-4 rounded-sm object-cover" />}
                            {p.nationality || p.country?.name}
                          </p>
                        )}
                        {p.source_url && (
                          <a href={p.source_url} target="_blank" rel="noreferrer" className="mt-0.5 inline-block text-xs text-sky-300 hover:underline">Voir la source</a>
                        )}
                      </div>
                    </div>
                    <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">{p.tribute_message}</p>
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" variant="outline" className="flex-1 rounded-full border-white/15" onClick={() => openEditDraft(p)} data-testid="admin-pending-open-btn">
                        <Pencil size={14} className="mr-1" /> Compléter
                      </Button>
                      <Button size="sm" className="flex-1 rounded-full bg-emerald-500 text-white hover:bg-emerald-600" onClick={() => validatePending(p.id)} data-testid="admin-pending-validate-btn">
                        <Check size={15} className="mr-1" /> Valider
                      </Button>
                      <Button size="sm" variant="destructive" className="flex-1 rounded-full" onClick={() => rejectPending(p.id)} data-testid="admin-pending-reject-btn">
                        <X size={15} className="mr-1" /> Rejeter
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              );
            })()}
          </TabsContent>

          <TabsContent value="candles" className="mt-4">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">{candles.length} bougie(s)</p>
              <div className="flex items-center gap-2">
                {selectedCandleIds.length > 0 && (
                  <Button size="sm" variant="destructive" className="rounded-full" onClick={openBulkDelete} data-testid="admin-bulk-delete-btn">
                    <Trash2 size={15} className="mr-1.5" /> Supprimer la sélection ({selectedCandleIds.length})
                  </Button>
                )}
                <Button size="sm" className="rounded-full" onClick={openNewCandle} data-testid="admin-new-candle-btn">
                  <Plus size={15} className="mr-1.5" /> Nouvelle bougie
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-white/10">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10">
                    <TableHead className="w-10">
                      <Checkbox checked={allCandlesSelected} onCheckedChange={toggleSelectAllCandles} data-testid="admin-select-all-candles" aria-label="Tout sélectionner" />
                    </TableHead>
                    <SortHead k="name" label="Défunt" />
                    <SortHead k="death" label="Décès" />
                    <SortHead k="state" label="État" />
                    <TableHead>Forfait</TableHead>
                    <SortHead k="raised" label="Récolté" />
                    <TableHead>Ajuster (jours / heures)</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedCandles.map((c) => (
                    <TableRow key={c.id} className="border-white/5" data-testid="admin-candle-row" data-state={selectedCandleIds.includes(c.id) ? "selected" : undefined}>
                      <TableCell className="w-10">
                        <Checkbox checked={selectedCandleIds.includes(c.id)} onCheckedChange={() => toggleSelectCandle(c.id)} data-testid="admin-select-candle" aria-label="Sélectionner" />
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-3">
                          <div className={`shrink-0 rounded-full ${ringStyle(c.flag_colors) ? "p-[2px]" : ""}`} style={ringStyle(c.flag_colors) || undefined} title={c.country_name || ""}>
                            <img src={c.photo_base64 || "/candle.png"} alt={c.deceased_name} className={`h-10 w-10 rounded-full object-cover ${ringStyle(c.flag_colors) ? "border border-black/50" : "border-2 border-amber-300/80 shadow-[0_0_12px_rgba(251,191,36,0.3)]"}`} data-testid="admin-candle-photo" />
                          </div>
                          <div className="min-w-0">
                            <span className="block truncate">{c.deceased_name}</span>
                            {(c.birth_date || c.death_date) && (
                              <span className="block text-sm font-normal text-amber-200/70" data-testid="admin-candle-dates">{fmtDate(c.birth_date)} – {fmtDate(c.death_date)}</span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground" data-testid="admin-candle-death">{fmtDate(c.death_date) || "—"}</TableCell>
                      <TableCell>
                        <span className={c.paid ? (c.is_expired ? "text-muted-foreground" : "text-emerald-400") : "text-amber-300"}>
                          {c.paid ? (c.is_expired ? "Éteinte" : `${Math.round(c.remaining_seconds / 3600)}h`) : "Impayée"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Select value={c.plan || ""} onValueChange={(v) => changePlan(c.id, v)}>
                          <SelectTrigger className="h-8 w-[8.5rem]" data-testid="candle-plan-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
                          <SelectContent>
                            {CANDLE_PLANS.map((p) => (<SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>{euros(c.total_paid_cents)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Input type="number" className="h-8 w-14" placeholder="j" value={daysInput[c.id] || ""} onChange={(e) => setDaysInput({ ...daysInput, [c.id]: e.target.value })} data-testid="set-days-input" />
                          <Input type="number" className="h-8 w-14" placeholder="h" value={hoursInput[c.id] || ""} onChange={(e) => setHoursInput({ ...hoursInput, [c.id]: e.target.value })} data-testid="set-hours-input" />
                          <Button size="sm" variant="outline" className="h-8 rounded-full border-white/15" onClick={() => setRemaining(c.id)} data-testid="set-hours-btn">
                            <Clock size={13} />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" title={c.has_blog ? "Article de blog publié — voir" : "Générer l'article de blog (IA) et publier"} className={`h-8 ${c.has_blog ? "text-emerald-400" : "text-red-400"}`} onClick={() => generateBlog(c)} disabled={blogGen === c.id} data-testid="admin-blog-btn">{blogGen === c.id ? <Loader2 size={14} className="animate-spin" /> : <Newspaper size={14} />}</Button>
                          <Button size="sm" variant="ghost" title="Voir le détail" className="h-8 text-foreground/70" onClick={() => openDetail(c)} data-testid="admin-detail-candle-btn"><Eye size={14} /></Button>
                          <Button size="sm" variant="ghost" title="Modifier la bougie" className="h-8 text-foreground/70" onClick={() => openEdit(c)} data-testid="admin-edit-candle-btn"><Pencil size={14} /></Button>
                          <Button size="sm" variant="ghost" title="Supprimer la bougie" className="h-8 text-destructive" onClick={() => openDelete(c)} data-testid="delete-candle-btn"><Trash2 size={14} /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="payments" className="mt-4">
            <div className="mb-4 rounded-2xl border border-white/10 bg-card/40 p-4 sm:p-5">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium"><TrendingUp size={16} className="text-sky-400" /> Revenus par période</div>
                  <p className="mt-0.5 text-xs text-muted-foreground" data-testid="revenue-subtitle">
                    {revGroup === "day" ? "Mois en cours, jour par jour" : revGroup === "month" ? "12 derniers mois" : "Par année"}
                  </p>
                </div>
                <div className="flex gap-1.5" data-testid="revenue-group-toggle">
                  {[{ v: "day", l: "Jour" }, { v: "month", l: "Mois" }, { v: "year", l: "Année" }].map((g) => (
                    <Button key={g.v} type="button" size="sm" variant={revGroup === g.v ? "default" : "outline"} className={`h-8 rounded-full ${revGroup === g.v ? "" : "border-white/15"}`} onClick={() => setRevGroup(g.v)} data-testid={`revenue-group-${g.v}`}>{g.l}</Button>
                  ))}
                </div>
              </div>
              <div className="h-64 w-full" data-testid="revenue-chart">
                {loadingSeries ? (
                  <div className="flex h-full items-center justify-center"><Loader2 className="animate-spin text-sky-400" /></div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={revSeries} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                      <defs>
                        <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#38bdf8" stopOpacity={0.95} />
                          <stop offset="100%" stopColor="#0284c7" stopOpacity={0.7} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                      <XAxis dataKey="label" tick={{ fill: "#a8a29e", fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={12} />
                      <YAxis tick={{ fill: "#a8a29e", fontSize: 11 }} axisLine={false} tickLine={false} width={44} tickFormatter={(v) => `${v}€`} />
                      <Tooltip
                        cursor={{ fill: "rgba(56,189,248,0.08)" }}
                        contentStyle={{ background: "#0b0a0a", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, color: "#f5f0e8" }}
                        formatter={(v, n, p) => [`${Number(v).toFixed(2)} € (${p.payload.count} paiement(s))`, "Revenus"]}
                      />
                      <Bar dataKey="euros" fill="url(#revGrad)" radius={[6, 6, 0, 0]} maxBarSize={48} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <span className="text-sm text-muted-foreground">Période :</span>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-40" data-testid="period-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tout</SelectItem>
                  <SelectItem value="today">Aujourd'hui</SelectItem>
                  <SelectItem value="7d">7 jours</SelectItem>
                  <SelectItem value="30d">30 jours</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" className="rounded-full border-white/15" onClick={exportRevenue} disabled={exportingRev} data-testid="export-revenue-btn">
                {exportingRev ? <Loader2 className="animate-spin" size={15} /> : <><Download size={15} className="mr-2" /> Exporter la période (.xlsx)</>}
              </Button>
              <div className="ml-auto rounded-full border border-white/10 bg-card/40 px-4 py-1.5 text-sm">
                Total : <span className="font-medium text-sky-400" data-testid="period-total">{euros(payData.total_cents)}</span> ({payData.count})
              </div>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-white/10">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10">
                    <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Mode</TableHead><TableHead className="text-right">Montant</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payData.payments.map((p) => (
                    <TableRow key={p.id} className="border-white/5">
                      <TableCell>{new Date(p.created_at).toLocaleString("fr-FR")}</TableCell>
                      <TableCell>{p.type === "light" ? "Allumage" : "Ravivage"}</TableCell>
                      <TableCell>{p.mode}</TableCell>
                      <TableCell className="text-right">{euros(p.amount)}</TableCell>
                    </TableRow>
                  ))}
                  {payData.payments.length === 0 && (
                    <TableRow><TableCell colSpan={4} className="py-6 text-center text-muted-foreground">Aucun paiement sur cette période.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="clients" className="mt-4">
            <div className="mb-4 flex items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">{clientData.total} client(s)</p>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="rounded-full border-destructive/40 text-destructive hover:bg-destructive/10" onClick={() => askDelete("cleanup-empty", null, "tous les clients sans bougie")} data-testid="admin-cleanup-empty-btn">
                  <Trash2 size={15} className="mr-1.5" /> Clients sans bougie
                </Button>
                <Button size="sm" className="rounded-full" onClick={openNewClient} data-testid="admin-new-client-btn">
                  <UserPlus size={15} className="mr-1.5" /> Nouveau client
                </Button>
              </div>
            </div>
            {clientData.groups.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground">Aucun client.</p>
            ) : clientData.groups.map((g) => (
              <div key={g.country} className="mb-6" data-testid="client-country-group">
                <div className="mb-2 flex items-center gap-2 text-sm font-medium">
                  <span className="text-lg">{g.country === "Non renseigné" ? "🏳️" : flagEmoji(g.country)}</span>
                  {g.country === "Non renseigné" ? "Pays non renseigné" : countryName(g.country)}
                  <span className="text-xs text-muted-foreground">({g.clients.length})</span>
                </div>
                <div className="overflow-x-auto rounded-2xl border border-white/10">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10">
                        <TableHead>Nom</TableHead><TableHead>Email</TableHead><TableHead>Bougies</TableHead><TableHead>Total</TableHead><TableHead className="text-right">Fiche</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {g.clients.map((cl) => (
                        <TableRow key={cl.id} className="border-white/5" data-testid="client-row">
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <img src={cl.avatar_base64 || "/logo.png"} alt={cl.name || "?"} className="h-8 w-8 shrink-0 rounded-full border border-amber-300/70 object-cover" data-testid="client-avatar" />
                              <span className="truncate">{cl.name || "—"}</span>
                              <span className="text-base leading-none" title={countryName(cl.country)} data-testid="client-flag">{flagEmoji(cl.country)}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground">{cl.email}</TableCell>
                          <TableCell>{cl.candle_count}</TableCell>
                          <TableCell>{euros(cl.total_paid_cents)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="outline" title="Modifier le client" className="h-8 rounded-full border-white/15" onClick={() => openClient(cl.id)} data-testid="client-open-btn"><Pencil size={13} /></Button>
                              <Button size="sm" variant="ghost" title="Supprimer le client" className="h-8 text-destructive" onClick={() => askDelete("user", cl.id, `« ${cl.name || cl.email} »`)} data-testid="client-delete-btn"><Trash2 size={13} /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="marketing" className="mt-4">
            <Tabs defaultValue="externe">
              <TabsList className="mb-5 grid w-full max-w-md grid-cols-2 gap-2 bg-transparent p-0">
                <TabsTrigger value="interne" data-testid="marketing-tab-interne" className="flex h-11 items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 text-sm font-medium text-muted-foreground transition-colors hover:bg-white/10 data-[state=active]:border-sky-400/50 data-[state=active]:bg-sky-500/15 data-[state=active]:text-sky-300">
                  <Users size={15} /> Marketing Interne
                </TabsTrigger>
                <TabsTrigger value="externe" data-testid="marketing-tab-externe" className="flex h-11 items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 text-sm font-medium text-muted-foreground transition-colors hover:bg-white/10 data-[state=active]:border-fuchsia-400/50 data-[state=active]:bg-fuchsia-500/15 data-[state=active]:text-fuchsia-300">
                  <Globe size={15} /> Marketing Externe
                </TabsTrigger>
              </TabsList>

              <TabsContent value="interne" className="mt-2">
                <p className="mb-4 text-sm text-muted-foreground">Communication avec vos clients existants : messages reçus et campagnes de relance.</p>

                <Tabs value={interneSub} onValueChange={setInterneSub} className="w-full">
                  <DraggableSubTabs storageKey="bdc_mkt_interne_order" testidPrefix="mi-tab" triggerClassName="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-medium text-muted-foreground cursor-grab active:cursor-grabbing transition-colors data-[state=active]:border-amber-300/50 data-[state=active]:bg-amber-300/15 data-[state=active]:text-amber-200" items={[["offre", "Offre de lancement"], ["messages", "Messages reçus"], ["relance", "Campagnes de relance"]]} />

                  <TabsContent value="offre" className="mt-5">
                <div className="mb-8 rounded-2xl border border-amber-300/30 bg-amber-300/[0.05] p-6" data-testid="launch-offer-panel">
                  <h3 className="mb-1 flex items-center gap-2 text-base font-semibold text-amber-200"><Gift size={16} /> Offre de lancement — 1 mois à {((Number(lo.price_cents) || 100) / 100).toFixed(2)} €</h3>
                  <p className="mb-4 text-sm text-muted-foreground">Filleul invité : 1er mois au tarif de lancement. Parrain : 1 mois offert dès que le filleul paie. Fixez une date de fin pour clôturer l'offre automatiquement.</p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label>Offre active</Label>
                      <div className="mt-1">
                        <Button type="button" variant="outline" className={`rounded-full ${lo.enabled !== false ? "border-emerald-400/50 text-emerald-300" : "border-white/15 text-muted-foreground"}`} onClick={() => setLO({ enabled: !(lo.enabled !== false) })} data-testid="launch-offer-toggle">
                          {lo.enabled !== false ? "Activée ✓" : "Désactivée"}
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label>Prix du 1er mois (€)</Label>
                      <Input type="number" step="0.01" min="0" value={((Number(lo.price_cents) || 100) / 100)} onChange={(e) => setLO({ price_cents: Math.round(Number(e.target.value || 0) * 100) })} data-testid="launch-offer-price-input" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label>Date de fin de l'offre</Label>
                      <Input type="datetime-local" value={toLocalInput(lo.ends_at)} onChange={(e) => setLO({ ends_at: e.target.value ? new Date(e.target.value).toISOString() : null })} data-testid="launch-offer-end-input" />
                      <p className="mt-1 text-xs text-muted-foreground">Laissez vide pour une offre sans date de fin. Après cette date, le tarif de lancement n'est plus proposé.</p>
                    </div>
                    <div className="sm:col-span-2">
                      <Label>Nom de l'offre (affiché aux visiteurs)</Label>
                      <Input value={lo.label || ""} onChange={(e) => setLO({ label: e.target.value })} placeholder="Ex : Bienvenue au Pape à Paris — bougie jusqu'à Noël" data-testid="launch-offer-label-input" />
                      <p className="mt-1 text-xs text-muted-foreground">Titre de l'offre spéciale. Laissez vide pour le texte par défaut.</p>
                    </div>
                    <div>
                      <Label>Offre exclusive</Label>
                      <div className="mt-1">
                        <Button type="button" variant="outline" className={`rounded-full ${lo.exclusive ? "border-emerald-400/50 text-emerald-300" : "border-white/15 text-muted-foreground"}`} onClick={() => setLO({ exclusive: !lo.exclusive })} data-testid="launch-offer-exclusive-toggle">
                          {lo.exclusive ? "Oui — masque les autres offres ✓" : "Non"}
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label>Bougie offerte jusqu'au</Label>
                      <Input type="datetime-local" value={toLocalInput(lo.candle_until)} onChange={(e) => setLO({ candle_until: e.target.value ? new Date(e.target.value).toISOString() : null })} data-testid="launch-offer-candle-until-input" />
                      <p className="mt-1 text-xs text-muted-foreground">La bougie reste allumée gratuitement jusqu'à cette date (ex : Noël). Laissez vide sinon.</p>
                    </div>
                  </div>
                  <Button className="mt-4 rounded-full" onClick={saveLaunchOffer} disabled={savingSettings} data-testid="launch-offer-save-btn">
                    {savingSettings ? <Loader2 className="animate-spin" size={16} /> : "Enregistrer l'offre"}
                  </Button>
                </div>

                  </TabsContent>

                  <TabsContent value="messages" className="mt-5">
                <h3 className="mb-3 flex items-center gap-2 text-base font-semibold text-foreground"><MessageSquare size={16} className="text-sky-400" /> Messages reçus</h3>
                <div className="space-y-3">
              {allMessages.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">Aucun message.</p>
              ) : allMessages.map((m) => (
                <div key={m.id} className="flex items-start justify-between gap-3 rounded-2xl border border-white/10 bg-card/40 p-4" data-testid="admin-global-message-item">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span className="text-sm font-medium text-sky-300">{m.author_name}</span>
                      <span className="text-xs text-muted-foreground">→ {m.deceased_name || "—"}</span>
                      <span className="text-xs text-muted-foreground">· {new Date(m.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="mt-1 text-sm text-foreground/90">{m.message}</p>
                    {m.photo_urls && m.photo_urls.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2" data-testid="admin-message-photos">
                        {m.photo_urls.map((u, i) => (
                          <div key={i} className="relative">
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${u}`} alt="" className="h-16 w-16 rounded-lg border border-white/10 object-cover" />
                            <button type="button" onClick={() => deleteCondolencePhoto(m.id, i)} className="absolute -right-1.5 -top-1.5 rounded-full bg-destructive p-0.5" title="Supprimer cette photo" data-testid={`admin-message-photo-delete-${i}`}><X size={12} /></button>
                          </div>
                        ))}
                      </div>
                    )}
                    {m.reaction_count > 0 && (
                      <p className="mt-1 text-xs text-amber-300/80">{m.reaction_count} réaction(s)</p>
                    )}
                  </div>
                  <Button size="sm" variant="ghost" className="h-8 shrink-0 text-destructive" onClick={() => askDelete("condolence", m.id, "ce message")} data-testid="admin-global-delete-message-btn"><Trash2 size={14} /></Button>
                </div>
              ))}
            </div>

                  </TabsContent>

                  <TabsContent value="relance" className="mt-5">
                <h3 className="mb-3 mt-8 flex items-center gap-2 text-base font-semibold text-foreground"><Send size={16} className="text-sky-400" /> Campagnes de relance clients</h3>
            <div className="grid gap-4 lg:grid-cols-[minmax(0,26rem)_1fr]">
            <div className="space-y-4 rounded-2xl border border-white/10 bg-card/40 p-6">
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><Send size={15} className="text-sky-400" /> Relance / campagne</div>
              <div>
                <Label>Type de campagne</Label>
                <Select value={campType} onValueChange={(v) => { setCampType(v); setCampSent(false); }}>
                  <SelectTrigger data-testid="campaign-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CAMPAIGN_TYPES.map((tp) => (<SelectItem key={tp.value} value={tp.value}>{tp.label}</SelectItem>))}
                  </SelectContent>
                </Select>
                {campType !== "general" && (
                  <p className="mt-1.5 text-xs text-sky-300/90" data-testid="campaign-segment-note">Seuls les clients concernés recevront ce message.</p>
                )}
              </div>
              <div>
                <Label>Canal</Label>
                <Select value={campChannel} onValueChange={setCampChannel}>
                  <SelectTrigger data-testid="campaign-channel-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">E-mail</SelectItem>
                    <SelectItem value="notification">Notification</SelectItem>
                    <SelectItem value="both">Les deux</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {campType === "general" && (
              <div>
                <Label>Destinataires</Label>
                <div className="mt-1.5 flex gap-2">
                  <Button type="button" size="sm" variant={campAll ? "default" : "outline"} className={`flex-1 rounded-full ${campAll ? "" : "border-white/15"}`} onClick={() => { setCampAll(true); setCampSelected([]); }} data-testid="campaign-all-btn">
                    Tous les clients ({allClientsFlat.length})
                  </Button>
                  <Button type="button" size="sm" variant={!campAll ? "default" : "outline"} className={`flex-1 rounded-full ${!campAll ? "" : "border-white/15"}`} onClick={() => setCampAll(false)} data-testid="campaign-select-btn">
                    Sélectionner
                  </Button>
                </div>
                {!campAll && (
                  <div className="mt-3">
                    <Input placeholder="Rechercher un client…" value={campSearch} onChange={(e) => setCampSearch(e.target.value)} className="mb-2" data-testid="campaign-search-input" />
                    <div className="max-h-56 space-y-1 overflow-y-auto rounded-xl border border-white/10 bg-black/20 p-2" data-testid="campaign-client-list">
                      {allClientsFlat.filter((cl) => {
                        const q = campSearch.trim().toLowerCase();
                        return !q || (cl.name || "").toLowerCase().includes(q) || (cl.email || "").toLowerCase().includes(q);
                      }).map((cl) => {
                        const checked = campSelected.includes(cl.id);
                        return (
                          <button key={cl.id} type="button" onClick={() => toggleCampClient(cl.id)} className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors ${checked ? "bg-sky-500/20 text-sky-200" : "hover:bg-white/5"}`} data-testid="campaign-client-option">
                            <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${checked ? "border-sky-400 bg-sky-500" : "border-white/30"}`}>
                              {checked && <Check size={12} className="text-white" />}
                            </span>
                            <img src={cl.avatar_base64 || "/logo.png"} alt="" className="h-6 w-6 shrink-0 rounded-full border border-amber-300/60 object-cover" />
                            <span className="min-w-0 flex-1 truncate">{cl.name || cl.email}</span>
                            <span className="text-base leading-none">{flagEmoji(cl.country)}</span>
                          </button>
                        );
                      })}
                      {allClientsFlat.length === 0 && <p className="px-2 py-3 text-center text-xs text-muted-foreground">Aucun client.</p>}
                    </div>
                    <p className="mt-1.5 text-xs text-muted-foreground" data-testid="campaign-selected-count">{campSelected.length} sélectionné(s)</p>
                  </div>
                )}
              </div>
              )}
              <div className="rounded-xl border border-white/10 bg-black/20 p-3 text-xs text-muted-foreground">
                <p className="mb-1 font-medium text-foreground/80">Aperçu du message (non modifiable) :</p>
                <p data-testid="campaign-preview">{CAMPAIGN_PREVIEWS[campType]}</p>
              </div>
              <Button className={`w-full rounded-full transition-colors ${campSent ? "bg-emerald-500 text-white hover:bg-emerald-500" : ""}`} onClick={sendCampaign} disabled={sendingCamp || campSent} data-testid="campaign-send-btn">
                {sendingCamp ? <Loader2 className="animate-spin" size={16} /> : campSent ? <><Check size={16} className="mr-2" /> Message envoyé</> : <><Send size={15} className="mr-2" /> Envoyer</>}
              </Button>
            </div>

            <div className="rounded-2xl border border-white/10 bg-card/40 p-4" data-testid="campaign-preview-panel">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium"><Users size={15} className="text-sky-400" /> Clients concernés</div>
                <span className="rounded-full bg-sky-500/15 px-2.5 py-0.5 text-xs font-medium text-sky-300" data-testid="campaign-preview-count">{campPreview.length}</span>
              </div>
              {loadingPreview ? (
                <div className="flex justify-center py-10"><Loader2 className="animate-spin text-sky-400" /></div>
              ) : campPreview.length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">Aucun client concerné pour cette campagne.</p>
              ) : (
                <div className="max-h-[26rem] space-y-2 overflow-y-auto pr-1" data-testid="campaign-preview-list">
                  {campPreview.map((tg) => (
                    <div key={tg.candle_id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/20 p-2.5" data-testid="campaign-preview-item">
                      <img
                        src={tg.has_photo ? candlePhotoUrl(tg.candle_id) : "/candle.png"}
                        alt={tg.deceased_name || ""}
                        className="h-11 w-11 shrink-0 rounded-full border-2 border-amber-300/80 object-cover shadow-[0_0_10px_rgba(251,191,36,0.25)]"
                        onError={(e) => { e.currentTarget.src = "/candle.png"; }}
                        data-testid="campaign-preview-photo"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium" data-testid="campaign-preview-name">{tg.deceased_name || "—"}</p>
                        <p className="truncate text-xs text-muted-foreground">{tg.owner_name || "—"}</p>
                      </div>
                      <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${tg.eternal ? "bg-sky-500/15 text-sky-300" : tg.is_expired || !tg.paid ? "bg-amber-400/15 text-amber-300" : "bg-emerald-500/15 text-emerald-300"}`} data-testid="campaign-preview-remaining">
                        {remainingLabel(tg)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            </div>
                  </TabsContent>
                </Tabs>
              </TabsContent>

              <TabsContent value="externe" className="mt-2">
                <MarketingExterne candles={candles} />
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="settings" className="mt-4">
            <Tabs value={settingsSub} onValueChange={setSettingsSub} className="w-full">
              <DraggableSubTabs storageKey="bdc_settings_order" testidPrefix="set-tab" triggerClassName="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-medium text-muted-foreground cursor-grab active:cursor-grabbing transition-colors data-[state=active]:border-sky-400/50 data-[state=active]:bg-sky-500/15 data-[state=active]:text-sky-300" items={[["tarifs", "Tarifs & offres"], ["connexions", "Connexions réseaux"], ["utm", "Liens UTM"], ["email", "Email (SMTP)"], ["seo", "SEO"]]} />

              <TabsContent value="tarifs" className="mt-5">
            <div className="mb-4 max-w-md rounded-2xl border border-amber-400/40 bg-amber-500/10 p-4" data-testid="tarifs-postinity-hint">
              <p className="text-sm font-semibold text-amber-200">Tarifs Postinity « De mon vivant »</p>
              <p className="mt-1 text-xs text-amber-100/85">Pour gérer le <strong>prix de la cérémonie</strong>, le <strong>prix des messages individuels</strong>, les <strong>promotions</strong> et les <strong>packs</strong> (avec sous-onglets Cérémonie / Message / Paramétrage), allez dans l'onglet <strong>« De mon vivant » → « Tarifs & accès »</strong> en haut de l'administration.</p>
              <p className="mt-1 text-[11px] text-amber-100/70">Cette page-ci concerne uniquement les bougies classiques.</p>
            </div>
            <div className="max-w-md space-y-4 rounded-2xl border border-white/10 bg-card/40 p-6">
              <div>
                <Label>Mode d'offre</Label>
                <Select value={settings.plans_mode || "single"} onValueChange={(v) => setSettings({ ...settings, plans_mode: v })}>
                  <SelectTrigger data-testid="plans-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Offre unique (1 € pour X heures)</SelectItem>
                    <SelectItem value="multi">Forfaits multiples (5 durées)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="mt-1 text-xs text-muted-foreground">En « Offre unique », les clients ne voient qu'une formule : le prix de base pour la durée de combustion ci-dessous.</p>
              </div>
              <div>
                <Label>Nom de l'offre unique</Label>
                <Input value={settings.single_label ?? "Hommage - Bougie"} onChange={(e) => setSettings({ ...settings, single_label: e.target.value })} placeholder="Ex. Hommage - Bougie" data-testid="single-label-input" />
                <p className="mt-1 text-xs text-muted-foreground">Affiché aux clients suivi de la durée. Aperçu : <span className="text-foreground/90">{(settings.single_label || "Hommage - Bougie")} {settings.burn_hours}h</span></p>
              </div>
              <div>
                <Label>Prix (en centimes)</Label>
                <Input type="number" value={settings.price_cents} onChange={(e) => setSettings({ ...settings, price_cents: e.target.value })} data-testid="price-input" />
                <p className="mt-1 text-xs text-muted-foreground">Soit {euros(Number(settings.price_cents))}</p>
              </div>
              <div>
                <Label>Durée de combustion (heures)</Label>
                <Input type="number" value={settings.burn_hours} onChange={(e) => setSettings({ ...settings, burn_hours: e.target.value })} data-testid="burn-hours-input" />
              </div>
              <div className="space-y-2 border-t border-white/10 pt-4">
                <Label>Prix des forfaits (en €) <span className="text-xs font-normal text-muted-foreground">— utilisés en mode « Forfaits multiples »</span></Label>
                {(settings.plans || []).map((p) => (
                  <div key={p.code} className="flex items-center justify-between gap-3" data-testid={`admin-plan-${p.code}`}>
                    <span className="text-sm">{p.label}</span>
                    <div className="flex items-center gap-1">
                      <Input type="number" step="0.5" min="0" className="w-24 text-right" value={(p.price_cents / 100).toString()} onChange={(e) => updatePlanPrice(p.code, e.target.value)} data-testid={`admin-plan-price-${p.code}`} />
                      <span className="text-sm text-muted-foreground">€</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="space-y-2 border-t border-white/10 pt-4">
                <Label>Calcul des revenus nets</Label>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <span className="text-xs text-muted-foreground">Frais (%)</span>
                    <Input type="number" step="0.1" min="0" value={settings.net_fee_percent ?? 1.5} onChange={(e) => setSettings({ ...settings, net_fee_percent: e.target.value })} data-testid="net-fee-percent-input" />
                  </div>
                  <div className="flex-1">
                    <span className="text-xs text-muted-foreground">Frais fixes / transaction (centimes)</span>
                    <Input type="number" min="0" value={settings.net_fee_fixed_cents ?? 25} onChange={(e) => setSettings({ ...settings, net_fee_fixed_cents: e.target.value })} data-testid="net-fee-fixed-input" />
                  </div>
                </div>
              </div>
              <div className="border-t border-white/10 pt-4">
                <Label>Suppressions protégées</Label>
                <p className="mt-1 text-xs text-muted-foreground">Toute suppression (bougie, message, utilisateur) requiert désormais le code super admin.</p>
              </div>
              {/* ===== Son du diaporama ===== */}
              <div className="border-t border-white/10 pt-4" data-testid="admin-diaporama-music">
                <Label className="flex items-center gap-2 text-sky-300"><Music size={16} /> Son du diaporama</Label>
                <p className="mt-1 mb-3 text-xs text-muted-foreground">Choisissez les musiques proposées aux utilisateurs, la présélection par défaut, et ajoutez vos propres musiques.</p>
                {!dmCfg ? (
                  <p className="text-xs text-muted-foreground">Chargement…</p>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <span className="text-xs font-medium text-white/80">Musiques intégrées (cochez celles à proposer)</span>
                      {dmCfg.builtins.map((b) => (
                        <div key={b.id} className="flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-black/20 px-3 py-2" data-testid={`admin-dm-builtin-${b.id}`}>
                          <div className="flex items-center gap-2">
                            <Checkbox checked={b.enabled} onCheckedChange={(v) => dmToggleBuiltin(b.id, !!v)} data-testid={`admin-dm-enable-${b.id}`} />
                            <span className="text-sm text-white/90">{b.label}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <audio src={dmSrc(b)} controls preload="none" className="h-8 w-40" />
                            <button type="button" onClick={() => dmSetDefault(b.id)} title="Définir par défaut" data-testid={`admin-dm-default-${b.id}`}
                              className={`rounded-full border px-2 py-1 text-[11px] font-medium ${dmCfg.default_choice === b.id ? "border-amber-400 bg-amber-500/20 text-amber-200" : "border-white/15 text-white/60 hover:bg-white/10"}`}>
                              <Star size={12} className="inline" /> {dmCfg.default_choice === b.id ? "Par défaut" : "Défaut"}
                            </button>
                            <button type="button" onClick={() => dmToggleBuiltin(b.id, false)} disabled={!b.enabled} title={b.enabled ? "Retirer de la liste proposée" : "Déjà retirée"} data-testid={`admin-dm-remove-${b.id}`}
                              className="rounded-full p-1.5 text-destructive hover:bg-white/10 disabled:opacity-30"><Trash2 size={14} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                    {dmCfg.custom.length > 0 && (
                      <div className="space-y-2">
                        <span className="text-xs font-medium text-white/80">Vos musiques personnalisées</span>
                        {dmCfg.custom.map((c) => (
                          <div key={c.id} className="flex items-center justify-between gap-2 rounded-lg border border-sky-400/20 bg-sky-500/[0.06] px-3 py-2" data-testid={`admin-dm-custom-${c.id}`}>
                            <span className="truncate text-sm text-white/90">🎵 {c.label}</span>
                            <div className="flex items-center gap-2">
                              <audio src={dmSrc(c)} controls preload="none" className="h-8 w-40" />
                              <button type="button" onClick={() => renameDmCustom(c)} className="rounded-full p-1.5 text-sky-300 hover:bg-white/10" title="Renommer" data-testid={`admin-dm-rename-${c.id}`}><Pencil size={13} /></button>
                              <button type="button" onClick={() => dmSetDefault(c.id)} title="Définir par défaut"
                                className={`rounded-full border px-2 py-1 text-[11px] font-medium ${dmCfg.default_choice === c.id ? "border-amber-400 bg-amber-500/20 text-amber-200" : "border-white/15 text-white/60 hover:bg-white/10"}`}>
                                <Star size={12} className="inline" /> {dmCfg.default_choice === c.id ? "Par défaut" : "Défaut"}
                              </button>
                              <button type="button" onClick={() => deleteDmCustom(c.id)} className="rounded-full p-1.5 text-destructive hover:bg-white/10" title="Supprimer" data-testid={`admin-dm-delete-${c.id}`}><Trash2 size={14} /></button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div>
                      <Button size="sm" variant="outline" className="rounded-full border-sky-400/40 text-sky-200 hover:bg-sky-500/10" onClick={() => dmInputRef.current?.click()} disabled={dmBusy} data-testid="admin-dm-upload-btn">
                        {dmBusy ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Plus size={13} className="mr-1.5" />}{dmBusy ? `Envoi ${dmProgress}%` : "Ajouter une musique"}
                      </Button>
                      <input ref={dmInputRef} type="file" accept=".mp3,.m4a,audio/*" className="hidden" onChange={uploadDmMusic} data-testid="admin-dm-input" />
                      <p className="mt-1 text-[11px] text-muted-foreground">MP3, max 20 Mo. La musique sera proposée à tous les utilisateurs.</p>
                    </div>
                  </div>
                )}
              </div>

              <Button className="w-full rounded-full" onClick={saveSettings} disabled={savingSettings} data-testid="save-settings-btn">
                {savingSettings ? <Loader2 className="animate-spin" size={16} /> : "Enregistrer"}
              </Button>
            </div>
              </TabsContent>

              <TabsContent value="connexions" className="mt-5"><SocialConnectionsPanel /></TabsContent>
              <TabsContent value="utm" className="mt-5"><UtmBuilderPanel /></TabsContent>

              <TabsContent value="seo" className="mt-5">
            <div className="max-w-md space-y-4 rounded-2xl border border-white/10 bg-card/40 p-6" data-testid="seo-verification-block">
                <Label className="flex items-center gap-2 text-sky-300"><Globe size={15} /> Vérification moteurs (SEO)</Label>
                <p className="mt-1 mb-3 text-xs text-muted-foreground">Collez ici le code fourni par Google Search Console / Bing. Il sera automatiquement inséré dans le site (méthode balise HTML). Collez soit le code seul, soit la balise complète.</p>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Google Search Console</Label>
                    <Input placeholder="ex. aBcD1234... (ou la balise meta complète)" value={settings.seo_google_verification || ""} onChange={(e) => setSettings({ ...settings, seo_google_verification: extractVerif(e.target.value) })} data-testid="seo-google-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Bing Webmaster</Label>
                    <Input placeholder="ex. 1A2B3C..." value={settings.seo_bing_verification || ""} onChange={(e) => setSettings({ ...settings, seo_bing_verification: extractVerif(e.target.value) })} data-testid="seo-bing-input" />
                  </div>
                </div>
              <Button className="mt-4 w-full rounded-full" onClick={saveSettings} disabled={savingSettings} data-testid="save-seo-btn">
                {savingSettings ? <Loader2 className="animate-spin" size={16} /> : "Enregistrer"}
              </Button>
            </div>
              </TabsContent>

              <TabsContent value="email" className="mt-5">
            {/* ===== Réglages Email (SMTP) ===== */}
            <div className="max-w-md space-y-4 rounded-2xl border border-white/10 bg-card/40 p-6" data-testid="admin-email-settings">
              <div className="flex items-center gap-2">
                <Mail size={18} className="text-sky-300" />
                <h3 className="text-lg font-medium">Email (envoi SMTP)</h3>
              </div>
              <p className="text-xs text-muted-foreground">
                Ces réglages servent à envoyer les e-mails (condoléances, campagnes). Ils sont stockés en base et
                fonctionnent en production sans redéploiement. Renseigne le <strong>mot de passe</strong> de la boîte
                d'envoi (le seul champ manquant), puis enregistre.
              </p>

              {emailCfg && (
                <>
                  <div>
                    <Label>Serveur SMTP (hôte)</Label>
                    <Input value={emailCfg.smtp_host || ""} onChange={(e) => setEmailCfg({ ...emailCfg, smtp_host: e.target.value })} placeholder="smtp.securemail.pro" data-testid="email-smtp-host" />
                  </div>
                  <div>
                    <Label>Port</Label>
                    <Input type="number" value={emailCfg.smtp_port || 465} onChange={(e) => setEmailCfg({ ...emailCfg, smtp_port: e.target.value })} placeholder="465" data-testid="email-smtp-port" />
                    <p className="mt-1 text-xs text-muted-foreground">465 = SSL (recommandé).</p>
                  </div>
                  <div>
                    <Label>Identifiant / compte d'envoi (SMTP_USER)</Label>
                    <Input value={emailCfg.smtp_user || ""} onChange={(e) => setEmailCfg({ ...emailCfg, smtp_user: e.target.value })} placeholder="bougie@bougieducoeur.com" data-testid="email-smtp-user" />
                  </div>
                  <div>
                    <Label>Mot de passe de la boîte d'envoi (SMTP_PASSWORD)</Label>
                    <PasswordInput value={emailPwd} onChange={(e) => setEmailPwd(e.target.value)} placeholder={emailCfg.password_set ? "•••••••• (déjà défini — laisser vide pour conserver)" : "À renseigner"} data-testid="email-smtp-password" />
                    <p className="mt-1 text-xs" style={{ color: emailCfg.password_set ? "#34d399" : "#f59e0b" }}>
                      {emailCfg.password_set ? "✓ Mot de passe déjà enregistré" : "⚠ Aucun mot de passe — les e-mails ne partiront pas tant qu'il n'est pas saisi"}
                    </p>
                  </div>
                  <div>
                    <Label>Adresse expéditeur (From)</Label>
                    <Input value={emailCfg.smtp_from || ""} onChange={(e) => setEmailCfg({ ...emailCfg, smtp_from: e.target.value })} placeholder="bougie@bougieducoeur.com" data-testid="email-smtp-from" />
                  </div>
                  <div>
                    <Label>Copie systématique (Cc admin)</Label>
                    <Input value={emailCfg.notify_cc || ""} onChange={(e) => setEmailCfg({ ...emailCfg, notify_cc: e.target.value })} placeholder="bougie@bougieducoeur.com" data-testid="email-notify-cc" />
                  </div>
                  <div>
                    <Label>URL publique du site</Label>
                    <Input value={emailCfg.public_app_url || ""} onChange={(e) => setEmailCfg({ ...emailCfg, public_app_url: e.target.value })} placeholder="https://bougieducoeur.com" data-testid="email-public-url" />
                  </div>
                  <p className="text-xs text-muted-foreground">Source actuelle : <span className="text-foreground/90">{emailCfg.source}</span></p>

                  <Button className="w-full rounded-full" onClick={saveEmailCfg} disabled={savingEmail} data-testid="save-email-settings-btn">
                    {savingEmail ? <Loader2 className="animate-spin" size={16} /> : "Enregistrer les réglages e-mail"}
                  </Button>

                  <div className="border-t border-white/10 pt-4">
                    <Label>Tester l'envoi</Label>
                    <div className="mt-1 flex gap-2">
                      <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="adresse@exemple.com" data-testid="email-test-to" />
                      <Button variant="outline" className="shrink-0 rounded-full border-white/15" onClick={sendTestEmail} disabled={emailBusy} data-testid="send-test-email-btn">
                        <Send size={14} className="mr-1.5" /> Envoyer
                      </Button>
                    </div>
                    <Button variant="ghost" className="mt-2 w-full rounded-full text-muted-foreground" onClick={runEmailDiag} disabled={emailBusy} data-testid="run-email-diag-btn">
                      {emailBusy ? <Loader2 className="animate-spin" size={14} /> : <><Shield size={14} className="mr-1.5" /> Diagnostic de connexion</>}
                    </Button>
                    {emailDiag && (
                      <div className="mt-2 rounded-lg border border-white/10 bg-black/30 p-3 text-xs" data-testid="email-diag-result">
                        <p>Connexion serveur : <span className={emailDiag.tcp_connect === "ok" ? "text-emerald-400" : "text-red-400"}>{emailDiag.tcp_connect}</span></p>
                        <p>Authentification : <span className={emailDiag.smtp_login === "ok" ? "text-emerald-400" : "text-red-400"}>{emailDiag.smtp_login}</span></p>
                        <p className="mt-1 text-muted-foreground">Identifiant : {emailDiag.config_present?.smtp_user ? "✓" : "✗"} · Mot de passe : {emailDiag.config_present?.smtp_password ? "✓" : "✗"}</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="vivant" className="mt-4">
            <Tabs defaultValue="v-bougies" className="w-full">
              <TabsList className="mb-4 flex w-full flex-nowrap gap-1 overflow-x-auto bg-transparent p-0 pb-1" data-testid="living-subtabs">
                <TabsTrigger value="v-bougies" className="shrink-0 whitespace-nowrap rounded-full border border-white/10 px-4 data-[state=active]:bg-amber-500 data-[state=active]:text-black" data-testid="living-subtab-bougies"><Flame size={14} className="mr-1.5" /> Bougies</TabsTrigger>
                <TabsTrigger value="v-reglages" className="shrink-0 whitespace-nowrap rounded-full border border-white/10 px-4 data-[state=active]:bg-amber-500 data-[state=active]:text-black" data-testid="living-subtab-reglages"><Settings size={14} className="mr-1.5" /> Réglages</TabsTrigger>
                <TabsTrigger value="v-tarifs" className="shrink-0 whitespace-nowrap rounded-full border border-white/10 px-4 data-[state=active]:bg-amber-500 data-[state=active]:text-black" data-testid="living-subtab-tarifs"><Euro size={14} className="mr-1.5" /> Tarifs & accès</TabsTrigger>
              </TabsList>

              <TabsContent value="v-bougies">
            <div className="mb-6 rounded-2xl border border-white/10 bg-card/40 p-4" data-testid="admin-living-list-card">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="flex items-center gap-2 text-base font-semibold text-foreground"><Flame size={16} className="text-amber-300" /> Bougies de mon vivant créées ({livingList.length})</h3>
              </div>
              {livingList.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground" data-testid="admin-living-empty">Aucune bougie de mon vivant pour le moment.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10">
                        <TableHead>Titulaire</TableHead>
                        <TableHead>Contenu</TableHead>
                        <TableHead>Cérémonie</TableHead>
                        <TableHead>État</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {livingList.map((lc) => (
                        <TableRow key={lc.id} className="cursor-pointer border-white/5 transition-colors hover:bg-white/[0.04]" data-testid="admin-living-row" onClick={() => openLivingDetail(lc)} title="Voir toutes les informations">
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-3">
                              <img src={lc.photo_base64 || "/candle.png"} alt="" className="h-10 w-10 shrink-0 rounded-full border-2 border-amber-300/70 object-cover" />
                              <div className="min-w-0">
                                <span className="block truncate">{lc.display_name || "—"}</span>
                                <span className="block truncate text-xs text-muted-foreground">{lc.owner_email || lc.owner_name || ""}</span>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                            {lc.photos_count} photo{lc.photos_count > 1 ? "s" : ""}{lc.has_video ? " · vidéo" : ""}<br />
                            {lc.garantes_count} garant{lc.garantes_count > 1 ? "s" : ""} · {lc.messages_count} message{lc.messages_count > 1 ? "s" : ""}
                          </TableCell>
                          <TableCell className="text-sm">
                            {lc.ceremony_enabled ? (
                              <span className="text-amber-300">Activée<br /><span className="text-xs text-muted-foreground">{lc.ceremony_payer === "owner" ? (lc.ceremony_paid ? "titulaire payé" : "titulaire à payer") : "contacts payent"}</span></span>
                            ) : <span className="text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell>
                            {lc.published ? <span className="text-emerald-400">Publiée</span> : <span className="text-amber-300">Brouillon</span>}
                            {lc.death_date && <span className="block text-xs text-muted-foreground">Décès : {fmtDate(lc.death_date)}</span>}
                            {lc.garantes_count > 0 && (
                              lc.death_confirmed
                                ? <span className="mt-0.5 block text-xs text-emerald-300" data-testid="admin-living-death-confirmed">✓ Décès confirmé par les {lc.garantes_count} garants</span>
                                : <span className="mt-0.5 block text-xs text-amber-300" data-testid="admin-living-garantes-status">Garants : {lc.garantes_confirmed}/{lc.garantes_count} confirmé{lc.garantes_confirmed > 1 ? "s" : ""}</span>
                            )}
                            {lc.messages_sent && <span className="block text-xs text-emerald-400">Messages envoyés ✓</span>}
                          </TableCell>
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex flex-wrap justify-end gap-1">
                              {lc.death_confirmed && !lc.messages_sent && (
                                <Button size="sm" className="h-8 rounded-full bg-amber-500 text-black hover:bg-amber-400" onClick={() => sendLivingMessages(lc)} data-testid="admin-living-send-messages-btn"><Send size={13} className="mr-1.5" /> Contrôler &amp; envoyer</Button>
                              )}
                              {!lc.published ? (
                                <Button size="sm" variant="outline" className="h-8 rounded-full border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" onClick={() => openPublish(lc)} data-testid="admin-living-publish-btn">Publier</Button>
                              ) : (
                                <>
                                  <Button size="sm" variant="ghost" title="Voir la bougie publiée" className="h-8 text-foreground/70" onClick={() => window.open(`/candle/${lc.published_candle_id}`, "_blank")} data-testid="admin-living-view-btn"><Eye size={14} /></Button>
                                  <Button size="sm" variant="outline" className="h-8 rounded-full border-amber-500/40 text-amber-300 hover:bg-amber-500/10" onClick={() => openPublish(lc)} data-testid="admin-living-setdate-btn">Date décès</Button>
                                  <Button size="sm" variant="outline" className="h-8 rounded-full border-white/15" onClick={() => doUnpublish(lc)} data-testid="admin-living-unpublish-btn">Dépublier</Button>
                                </>
                              )}
                              <Button size="sm" variant="ghost" title="Supprimer" className="h-8 text-destructive" onClick={() => askDelete("living", lc.id, `la bougie de mon vivant de « ${lc.display_name || lc.owner_email} »`)} data-testid="admin-living-delete-btn"><Trash2 size={14} /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
              </TabsContent>

              <Dialog open={lcDetailOpen} onOpenChange={setLcDetailOpen}>
                <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto bg-neutral-950" data-testid="admin-living-detail-dialog">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-amber-200"><Flame size={18} /> Détail — Ma bougie de mon vivant</DialogTitle>
                    <DialogDescription>Toutes les informations saisies par le titulaire, à l'identique de son espace.</DialogDescription>
                  </DialogHeader>
                  {lcDetailBusy || !lcDetail ? (
                    <div className="flex justify-center py-10"><Loader2 className="animate-spin text-amber-300" /></div>
                  ) : (
                    <div className="space-y-4 text-sm">
                      {/* Titulaire + état */}
                      <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/20 p-3">
                        <img src={(lcDetail.photos && lcDetail.photos[0]) || "/candle.png"} alt="" className="h-12 w-12 shrink-0 rounded-full border-2 border-amber-300/70 object-cover" />
                        <div className="min-w-0">
                          <div className="font-semibold text-foreground">{lcDetail.display_name || "—"}</div>
                          <div className="truncate text-xs text-muted-foreground">{lcDetail.owner_email || lcDetail.owner_name}</div>
                          <div className="mt-0.5 text-xs">{lcDetail.published ? <span className="text-emerald-400">Publiée</span> : <span className="text-amber-300">Brouillon</span>}{lcDetail.death_date ? <span className="text-muted-foreground"> · Décès : {fmtDate(lcDetail.death_date)}</span> : ""}</div>
                        </div>
                      </div>

                      {/* 1. Préparer ma cérémonie */}
                      <div className="rounded-xl border border-amber-400/25 bg-amber-500/[0.05] p-3" data-testid="lc-detail-ceremonie">
                        <div className="mb-2 flex items-center gap-2 font-semibold text-amber-200"><span className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-500/20 text-[11px] font-bold text-amber-300">1</span><Flame size={15} /> Préparer ma cérémonie <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-bold text-emerald-300">GRATUIT</span></div>
                        {(() => { const c = lcDetail.ceremony || {}; const empty = !((c.text || "").trim()) && !(c.songs || []).length && !(c.photos || []).length && !c.video_ready; return empty ? (
                          <p className="text-xs text-muted-foreground">Aucune information de cérémonie renseignée.</p>
                        ) : (
                          <div className="space-y-2">
                            {(c.text || "").trim() && <div><span className="text-xs text-muted-foreground">Message :</span><p className="whitespace-pre-wrap text-foreground/90">{c.text}</p></div>}
                            {(c.songs || []).length > 0 && <div><span className="text-xs text-muted-foreground">Chansons :</span><ul className="mt-1 list-disc pl-5 text-foreground/90">{c.songs.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
                            {(c.photos || []).length > 0 && <div><span className="text-xs text-muted-foreground">Photos de cérémonie ({c.photos.length}) :</span><div className="mt-1 flex flex-wrap gap-2">{c.photos.map((p, i) => <img key={i} src={p} alt="" className="h-16 w-16 rounded-lg border border-white/10 object-cover" />)}</div></div>}
                            {c.video_ready && <div className="inline-flex items-center gap-1.5 rounded-full bg-sky-500/15 px-2 py-1 text-xs text-sky-200"><Check size={12} /> Vidéo de cérémonie enregistrée</div>}
                          </div>
                        ); })()}
                      </div>

                      {/* 2. Message personnel */}
                      <div className="rounded-xl border border-white/10 bg-black/20 p-3" data-testid="lc-detail-messages">
                        <div className="mb-2 flex items-center gap-2 font-semibold text-sky-300"><span className="flex h-5 w-5 items-center justify-center rounded-full bg-sky-500/20 text-[11px] font-bold text-sky-300">2</span><Mail size={15} /> Message personnel ({(lcDetail.messages || []).length})</div>
                        {(lcDetail.messages || []).length === 0 ? (
                          <p className="text-xs text-muted-foreground">Aucun message individuel.</p>
                        ) : (
                          <div className="space-y-2">{lcDetail.messages.map((m) => (
                            <div key={m.id} className="rounded-lg border border-white/10 bg-black/30 p-2.5">
                              <div className="flex items-center justify-between gap-2">
                                <span className="truncate font-medium text-foreground">{m.title || "(Sans titre)"} {m.free && <span className="ml-1 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] text-emerald-300">Gratuit</span>}</span>
                                <span className="shrink-0 text-[11px] text-muted-foreground">{m.sent_at ? "envoyé ✓" : "non envoyé"}</span>
                              </div>
                              <div className="text-xs text-muted-foreground">Pour {m.recipient_name || m.recipient_email} · {m.recipient_email}</div>
                              {(m.text || "").trim() && <p className="mt-1 whitespace-pre-wrap text-foreground/90">{m.text}</p>}
                              {(m.photos || []).length > 0 && <div className="mt-1 flex flex-wrap gap-2">{m.photos.map((p, i) => <img key={i} src={p} alt="" className="h-14 w-14 rounded-lg border border-white/10 object-cover" />)}</div>}
                              <div className="mt-1 text-[11px] text-muted-foreground">{m.photos_count} photo{m.photos_count > 1 ? "s" : ""}{m.video_ready ? " · vidéo" : ""}</div>
                            </div>
                          ))}</div>
                        )}
                      </div>

                      {/* 3. Ma bougie */}
                      <div className="rounded-xl border border-white/10 bg-black/20 p-3" data-testid="lc-detail-bougie">
                        <div className="mb-2 flex items-center gap-2 font-semibold text-amber-200"><span className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-500/20 text-[11px] font-bold text-amber-300">3</span><Flame size={15} /> Ma bougie</div>
                        <div className="space-y-2">
                          <div><span className="text-xs text-muted-foreground">Nom affiché :</span> <span className="text-foreground/90">{lcDetail.display_name || "—"}</span></div>
                          {(lcDetail.citation || "").trim() && <div><span className="text-xs text-muted-foreground">Mot du cœur :</span><p className="whitespace-pre-wrap text-foreground/90">{lcDetail.citation}</p></div>}
                          {(lcDetail.photos || []).length > 0 && <div><span className="text-xs text-muted-foreground">Diaporama ({lcDetail.photos.length}) :</span><div className="mt-1 flex flex-wrap gap-2">{lcDetail.photos.map((p, i) => <img key={i} src={p} alt="" className="h-16 w-16 rounded-lg border border-white/10 object-cover" />)}</div></div>}
                          {lcDetail.video_ready && <div className="inline-flex items-center gap-1.5 rounded-full bg-sky-500/15 px-2 py-1 text-xs text-sky-200"><Check size={12} /> Vidéo hommage enregistrée</div>}
                        </div>
                      </div>

                      {/* 4. Mes garants */}
                      <div className="rounded-xl border border-white/10 bg-black/20 p-3" data-testid="lc-detail-garants">
                        <div className="mb-2 flex items-center gap-2 font-semibold text-amber-200"><span className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-500/20 text-[11px] font-bold text-amber-300">4</span><ShieldCheck size={15} /> Mes garants</div>
                        {(lcDetail.garantes || []).filter((g) => (g.email || "").trim() || (g.name || "").trim()).length === 0 ? (
                          <p className="text-xs text-muted-foreground">Aucun garant désigné.</p>
                        ) : (
                          <div className="space-y-1.5">{lcDetail.garantes.filter((g) => (g.email || "").trim() || (g.name || "").trim()).map((g, i) => (
                            <div key={i} className="flex items-center justify-between rounded-lg border border-white/10 bg-black/30 px-2.5 py-1.5">
                              <span className="text-foreground/90">{g.name || "—"} <span className="text-xs text-muted-foreground">· {g.email}</span></span>
                              {g.code && <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] text-emerald-300">lien envoyé</span>}
                            </div>
                          ))}</div>
                        )}
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>

              <TabsContent value="v-reglages">
            <div className="max-w-xl space-y-4">
              <div className="rounded-2xl border border-amber-400/25 bg-amber-500/[0.06] p-5" data-testid="living-candle-admin-card">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 text-amber-200"><Flame size={18} /><span className="text-sm font-semibold">Ma bougie de mon vivant</span></div>
                    <p className="mt-1 text-xs text-muted-foreground">Espace privé (photos en diaporama + citation + personne garante). Tant que ce n'est pas publié pour tous, seuls les profils de test ci-dessous y ont accès.</p>
                  </div>
                  <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium ${settings.living_candle_enabled ? "bg-emerald-500/15 text-emerald-300" : "bg-white/10 text-muted-foreground"}`}>{settings.living_candle_enabled ? "Publiée (tous)" : "Masquée"}</span>
                </div>
                <Button size="sm" className={`mt-3 rounded-full ${settings.living_candle_enabled ? "bg-white/10 text-foreground hover:bg-white/20" : "bg-amber-500 text-black hover:bg-amber-400"}`} onClick={toggleLivingCandle} data-testid="living-candle-toggle-btn">
                  {settings.living_candle_enabled ? "Masquer (repasser en test)" : "Publier pour tous les profils"}
                </Button>
              </div>

              <div className="rounded-2xl border border-white/10 bg-card/40 p-5" data-testid="living-test-emails-card">
                <div className="flex items-center gap-2 text-sky-300"><Lock size={16} /><span className="text-sm font-semibold">Profils de test autorisés</span></div>
                <p className="mb-3 mt-1 text-xs text-muted-foreground">Ajoutez l'e-mail d'un compte pour lui donner accès à la fonctionnalité, même quand elle est masquée pour tous.</p>
                <div className="flex gap-2">
                  <Input type="email" value={livingTestEmail} onChange={(e) => setLivingTestEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addLivingTestEmail()} placeholder="ex. prenom.nom@email.fr" data-testid="living-test-email-input" />
                  <Button className="rounded-full" onClick={addLivingTestEmail} data-testid="living-test-email-add-btn"><Plus size={15} className="mr-1" /> Ajouter</Button>
                </div>
                <div className="mt-3 space-y-2" data-testid="living-test-email-list">
                  {(settings.living_candle_test_emails || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground">Aucun profil de test.</p>
                  ) : (
                    (settings.living_candle_test_emails || []).map((e) => (
                      <div key={e} className="flex items-center justify-between rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm" data-testid="living-test-email-row">
                        <span className="text-foreground">{e}</span>
                        <button onClick={() => removeLivingTestEmail(e)} className="text-destructive" data-testid="living-test-email-remove-btn"><X size={15} /></button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
              </TabsContent>

              <TabsContent value="v-tarifs">
            <div className="max-w-xl space-y-4">
              {livingP2 && (
                <div className="rounded-2xl border border-white/10 bg-card/40 p-5" data-testid="living-pricing-card">
                  <div className="flex items-center gap-2 text-amber-200"><Flame size={16} /><span className="text-sm font-semibold">Tarifs & accès (Phase 2)</span></div>
                  <p className="mb-3 mt-1 text-xs text-muted-foreground">Gérez séparément la cérémonie, les messages (prix, promos, packs) et le paramétrage d'accès. Les offres actives s'affichent automatiquement à l'utilisateur.</p>
                  <Tabs defaultValue="cer" className="w-full">
                    <TabsList className="mb-3 flex w-full gap-2 bg-transparent p-0">
                      <TabsTrigger value="cer" className="flex-1 rounded-full border border-white/10 data-[state=active]:bg-amber-500 data-[state=active]:text-black" data-testid="tarif-subtab-cer"><Flame size={14} className="mr-1.5" /> Cérémonie</TabsTrigger>
                      <TabsTrigger value="msg" className="flex-1 rounded-full border border-white/10 data-[state=active]:bg-sky-500 data-[state=active]:text-black" data-testid="tarif-subtab-msg"><Mail size={14} className="mr-1.5" /> Message</TabsTrigger>
                      <TabsTrigger value="param" className="flex-1 rounded-full border border-white/10 data-[state=active]:bg-white data-[state=active]:text-black" data-testid="tarif-subtab-param"><Euro size={14} className="mr-1.5" /> Paramétrage</TabsTrigger>
                    </TabsList>

                    {/* Onglet Cérémonie : prix + qui paie + promo */}
                    <TabsContent value="cer" className="space-y-3" data-testid="tarif-tab-cer">
                      <div><Label className="text-xs">Prix dossier cérémonie (€)</Label><Input type="number" step="1" value={livingP2.ceremony_eur} onChange={(e) => setLivingP2((p) => ({ ...p, ceremony_eur: e.target.value }))} data-testid="living-price-ceremony" /></div>
                      <div>
                        <Label className="text-xs">Variante « qui paie » du dossier cérémonie</Label>
                        <select value={livingP2.ceremony_payer} onChange={(e) => setLivingP2((p) => ({ ...p, ceremony_payer: e.target.value }))} className="mt-1 w-full rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm" data-testid="living-ceremony-payer">
                          <option value="both">Le titulaire choisit (les deux)</option>
                          <option value="owner">Le titulaire paie</option>
                          <option value="contacts">Les contacts paient</option>
                        </select>
                      </div>
                      <div className="rounded-xl border border-white/10 bg-black/20 p-3" data-testid="promo-ceremony">
                        <label className="flex items-center gap-2 text-xs font-medium text-white">
                          <input type="checkbox" checked={!!livingP2.cer_promo_active} onChange={(e) => setLivingP2((p) => ({ ...p, cer_promo_active: e.target.checked }))} className="h-4 w-4 accent-amber-500" data-testid="promo-cer-active" /> Promo sur la cérémonie
                        </label>
                        {livingP2.cer_promo_active && (
                          <div className="mt-2 grid gap-2 sm:grid-cols-2">
                            <select value={livingP2.cer_promo_type} onChange={(e) => setLivingP2((p) => ({ ...p, cer_promo_type: e.target.value }))} className="rounded-md border border-white/15 bg-black/30 px-2 py-1.5 text-xs" data-testid="promo-cer-type"><option value="price">Prix promo (€)</option><option value="percent">Remise (%)</option></select>
                            {livingP2.cer_promo_type === "percent"
                              ? <Input type="number" min="0" max="100" value={livingP2.cer_promo_percent} onChange={(e) => setLivingP2((p) => ({ ...p, cer_promo_percent: e.target.value }))} placeholder="% remise" data-testid="promo-cer-percent" />
                              : <Input type="number" step="1" value={livingP2.cer_promo_eur} onChange={(e) => setLivingP2((p) => ({ ...p, cer_promo_eur: e.target.value }))} placeholder="Prix promo €" data-testid="promo-cer-price" />}
                            <div><Label className="text-[10px]">Début</Label><Input type="date" value={livingP2.cer_promo_start} onChange={(e) => setLivingP2((p) => ({ ...p, cer_promo_start: e.target.value }))} data-testid="promo-cer-start" /></div>
                            <div><Label className="text-[10px]">Fin</Label><Input type="date" value={livingP2.cer_promo_end} onChange={(e) => setLivingP2((p) => ({ ...p, cer_promo_end: e.target.value }))} data-testid="promo-cer-end" /></div>
                          </div>
                        )}
                      </div>
                    </TabsContent>

                    {/* Onglet Message : prix + promo + packs */}
                    <TabsContent value="msg" className="space-y-3" data-testid="tarif-tab-msg">
                      <div><Label className="text-xs">Prix message individuel (€)</Label><Input type="number" step="1" value={livingP2.message_eur} onChange={(e) => setLivingP2((p) => ({ ...p, message_eur: e.target.value }))} data-testid="living-price-message" /></div>
                      <div className="rounded-xl border border-white/10 bg-black/20 p-3" data-testid="promo-message">
                        <label className="flex items-center gap-2 text-xs font-medium text-white">
                          <input type="checkbox" checked={!!livingP2.msg_promo_active} onChange={(e) => setLivingP2((p) => ({ ...p, msg_promo_active: e.target.checked }))} className="h-4 w-4 accent-amber-500" data-testid="promo-msg-active" /> Promo sur les messages individuels
                        </label>
                        {livingP2.msg_promo_active && (
                          <div className="mt-2 grid gap-2 sm:grid-cols-2">
                            <select value={livingP2.msg_promo_type} onChange={(e) => setLivingP2((p) => ({ ...p, msg_promo_type: e.target.value }))} className="rounded-md border border-white/15 bg-black/30 px-2 py-1.5 text-xs" data-testid="promo-msg-type"><option value="price">Prix promo (€)</option><option value="percent">Remise (%)</option></select>
                            {livingP2.msg_promo_type === "percent"
                              ? <Input type="number" min="0" max="100" value={livingP2.msg_promo_percent} onChange={(e) => setLivingP2((p) => ({ ...p, msg_promo_percent: e.target.value }))} placeholder="% remise" data-testid="promo-msg-percent" />
                              : <Input type="number" step="1" value={livingP2.msg_promo_eur} onChange={(e) => setLivingP2((p) => ({ ...p, msg_promo_eur: e.target.value }))} placeholder="Prix promo €" data-testid="promo-msg-price" />}
                            <div><Label className="text-[10px]">Début</Label><Input type="date" value={livingP2.msg_promo_start} onChange={(e) => setLivingP2((p) => ({ ...p, msg_promo_start: e.target.value }))} data-testid="promo-msg-start" /></div>
                            <div><Label className="text-[10px]">Fin</Label><Input type="date" value={livingP2.msg_promo_end} onChange={(e) => setLivingP2((p) => ({ ...p, msg_promo_end: e.target.value }))} data-testid="promo-msg-end" /></div>
                          </div>
                        )}
                      </div>
                      <div className="rounded-xl border border-white/10 bg-black/20 p-3" data-testid="promo-bundles">
                        <div className="mb-2 flex items-center justify-between">
                          <span className="text-xs font-medium text-white">Lots de messages (packs)</span>
                          <Button size="sm" variant="outline" className="h-7 rounded-full border-white/20 text-xs" onClick={() => setLivingP2((p) => ({ ...p, bundles: [...(p.bundles || []), { id: `bundle_${Date.now()}`, label: "", qty: 5, price_eur: 20, bonus: 0 }] }))} data-testid="promo-bundle-add"><Plus size={12} className="mr-1" /> Ajouter</Button>
                        </div>
                        {(livingP2.bundles || []).length === 0 && <p className="text-[11px] text-muted-foreground">Aucun lot. Ex. « 5 messages » à 20 € avec 1 offert.</p>}
                        <div className="space-y-2">
                          {(livingP2.bundles || []).map((b, i) => (
                            <div key={b.id || i} className="grid grid-cols-2 gap-2 rounded-lg border border-white/10 p-2 sm:grid-cols-5 sm:items-end" data-testid={`promo-bundle-row-${i}`}>
                              <div className="col-span-2 sm:col-span-1"><Label className="text-[10px]">Nom</Label><Input value={b.label} onChange={(e) => setLivingP2((p) => { const bs = [...p.bundles]; bs[i] = { ...bs[i], label: e.target.value }; return { ...p, bundles: bs }; })} placeholder="Pack 5" /></div>
                              <div><Label className="text-[10px]">Messages</Label><Input type="number" min="1" value={b.qty} onChange={(e) => setLivingP2((p) => { const bs = [...p.bundles]; bs[i] = { ...bs[i], qty: e.target.value }; return { ...p, bundles: bs }; })} /></div>
                              <div><Label className="text-[10px]">Offerts</Label><Input type="number" min="0" value={b.bonus} onChange={(e) => setLivingP2((p) => { const bs = [...p.bundles]; bs[i] = { ...bs[i], bonus: e.target.value }; return { ...p, bundles: bs }; })} /></div>
                              <div><Label className="text-[10px]">Prix €</Label><Input type="number" step="1" value={b.price_eur} onChange={(e) => setLivingP2((p) => { const bs = [...p.bundles]; bs[i] = { ...bs[i], price_eur: e.target.value }; return { ...p, bundles: bs }; })} /></div>
                              <Button size="sm" variant="ghost" className="text-red-300 hover:text-red-200" onClick={() => setLivingP2((p) => ({ ...p, bundles: p.bundles.filter((_, idx) => idx !== i) }))} data-testid={`promo-bundle-del-${i}`}><Trash2 size={14} /></Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </TabsContent>

                    {/* Onglet Paramétrage : accès */}
                    <TabsContent value="param" className="space-y-3" data-testid="tarif-tab-param">
                      <div>
                        <Label className="text-xs">Ce que débloque le paiement</Label>
                        <select value={livingP2.access_type} onChange={(e) => setLivingP2((p) => ({ ...p, access_type: e.target.value }))} className="mt-1 w-full rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm" data-testid="living-access-type">
                          <option value="download">Consultation + téléchargement</option>
                          <option value="stream">Consultation en ligne uniquement</option>
                        </select>
                      </div>
                      <div><Label className="text-xs">Durée d'accès (jours, 0 = permanent)</Label><Input type="number" step="1" min="0" value={livingP2.access_days} onChange={(e) => setLivingP2((p) => ({ ...p, access_days: e.target.value }))} data-testid="living-access-days" /></div>
                    </TabsContent>
                  </Tabs>

                  <Button size="sm" className="mt-3 rounded-full bg-amber-500 text-black hover:bg-amber-400" onClick={saveLivingP2} data-testid="living-pricing-save-btn">Enregistrer les tarifs</Button>
                </div>
              )}
            </div>
              </TabsContent>
            </Tabs>
          </TabsContent>
          <TabsContent value="analytics" className="mt-4">
            <AdminAnalytics />
          </TabsContent>

        </Tabs>
      </div>

      {/* Edit name/message dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="glass border-white/10 sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="admin-edit-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium">Modifier la bougie</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">Nom du défunt et message d'hommage.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <div>
              <Label>Photo</Label>
              {ePhoto ? (
                <div className="relative mt-1 w-fit">
                  <img src={ePhoto} alt="aperçu" className="h-24 w-24 rounded-full border-2 border-amber-300/80 object-cover shadow-[0_0_20px_rgba(251,191,36,0.3)]" />
                  <button type="button" onClick={() => { setEPhoto(null); setEPhotoDirty(true); }} className="absolute -right-2 -top-2 rounded-full bg-destructive p-1" data-testid="admin-edit-remove-photo-btn"><X size={14} /></button>
                </div>
              ) : (
                <button type="button" onClick={() => editFileRef.current?.click()} className="mt-1 flex h-24 w-24 flex-col items-center justify-center rounded-full border-2 border-dashed border-amber-300/40 text-muted-foreground transition-colors hover:border-amber-300 hover:text-amber-300" data-testid="admin-edit-add-photo-btn">
                  <ImagePlus size={20} />
                  <span className="mt-1 text-xs">Ajouter</span>
                </button>
              )}
              <input ref={editFileRef} type="file" accept="image/*" className="hidden" onChange={onEditPhoto} />
            </div>
            <div>
              <Label>Nom du défunt</Label>
              <Input value={eName} onChange={(e) => setEName(titleCaseName(e.target.value))} data-testid="admin-edit-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Naissance</Label><Input type="date" value={eBirth} onChange={(e) => setEBirth(e.target.value)} data-testid="admin-edit-birth-date" /></div>
              <div><Label>Décès</Label><Input type="date" value={eDeath} onChange={(e) => setEDeath(e.target.value)} data-testid="admin-edit-death-date" /></div>
            </div>
            <div>
              <Label>Nationalité</Label>
              <select value={eNat} onChange={(e) => setENat(e.target.value)} data-testid="admin-edit-nationality-input" className="h-10 w-full rounded-md border border-white/15 bg-black/40 px-3 text-sm text-white outline-none focus:border-amber-300">
                <option value="">— Aucune —</option>
                {NATIONALITIES.map((n) => (<option key={n.value} value={n.value} className="bg-neutral-900">{n.label}</option>))}
              </select>
            </div>
            <div>
              <Label>Message d'hommage</Label>
              <Textarea rows={4} value={eMsg} onChange={(e) => setEMsg(e.target.value)} data-testid="admin-edit-message-input" />
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-3" data-testid="admin-edit-socials">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-amber-300">Réseaux sociaux du défunt</Label>
                <Button type="button" size="sm" variant="outline" className="h-8 rounded-full border-amber-300/40 text-amber-300 hover:bg-amber-400/10" onClick={discoverSocials} disabled={discoveringSocials} data-testid="admin-discover-socials-btn">
                  {discoveringSocials ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Globe size={14} className="mr-1.5" />} Détecter les réseaux
                </Button>
              </div>
              <div className="mt-1 mb-2 flex items-center justify-between gap-2">
                <p className="text-xs text-muted-foreground">Ouvrez chaque lien pour vérifier, puis affichez/masquez-le sur la fiche avec l'œil.</p>
                <button
                  type="button"
                  onClick={() => {
                    const keysWithVal = SOCIAL_PLATFORMS.filter((p) => (eSocials[p.key] || "").trim()).map((p) => p.key);
                    const anyVisible = keysWithVal.some((k) => !eSocialsHidden[k]);
                    setESocialsHidden(anyVisible ? Object.fromEntries(keysWithVal.map((k) => [k, true])) : {});
                  }}
                  className="shrink-0 text-xs text-sky-300 hover:underline"
                  data-testid="admin-social-toggle-all"
                >
                  Tout afficher / masquer
                </button>
              </div>
              <div className="space-y-2">
                {SOCIAL_PLATFORMS.map((p) => {
                  const val = eSocials[p.key] || "";
                  const hidden = !!eSocialsHidden[p.key];
                  return (
                    <div key={p.key} className="flex items-center gap-1.5">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: `${p.color}22`, color: p.color }}><p.Icon size={15} /></span>
                      <Input
                        value={val}
                        onChange={(e) => setESocials((s) => ({ ...s, [p.key]: e.target.value }))}
                        placeholder={p.placeholder}
                        className={`h-9 text-sm ${val && hidden ? "line-through opacity-50" : ""}`}
                        data-testid={`admin-social-${p.key}`}
                      />
                      <button
                        type="button"
                        disabled={!val}
                        onClick={() => window.open(val, "_blank", "noopener,width=1000,height=800")}
                        title="Ouvrir le lien dans une fenêtre pour vérifier"
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/10 text-muted-foreground transition-colors hover:text-sky-300 disabled:opacity-30"
                        data-testid={`admin-social-open-${p.key}`}
                      >
                        <ExternalLink size={15} />
                      </button>
                      <button
                        type="button"
                        disabled={!val}
                        onClick={() => setESocialsHidden((h) => ({ ...h, [p.key]: !h[p.key] }))}
                        title={hidden ? "Masqué sur la fiche — cliquer pour afficher" : "Affiché sur la fiche — cliquer pour masquer"}
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors disabled:opacity-30 ${hidden ? "border-white/10 text-muted-foreground hover:text-foreground" : "border-emerald-500/60 text-emerald-400"}`}
                        data-testid={`admin-social-visible-${p.key}`}
                      >
                        {hidden ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="sticky bottom-0 -mx-6 -mb-6 border-t border-white/10 bg-background/95 px-6 pt-3 pb-4 backdrop-blur">
            {editIsDraft ? (
              <div className="flex flex-col gap-2">
                <Button className="w-full rounded-full bg-emerald-500 text-white hover:bg-emerald-600" onClick={() => saveEdit(true)} disabled={savingEdit} data-testid="admin-edit-save-validate-btn">
                  {savingEdit ? <Loader2 className="animate-spin" size={16} /> : <><Check size={16} className="mr-1.5" /> Valider et publier</>}
                </Button>
                <Button variant="outline" className="w-full rounded-full border-white/15" onClick={() => saveEdit(false)} disabled={savingEdit} data-testid="admin-edit-save-btn">
                  {savingEdit ? <Loader2 className="animate-spin" size={16} /> : "Enregistrer (rester en brouillon)"}
                </Button>
              </div>
            ) : (
              <Button className="w-full rounded-full" onClick={() => saveEdit(false)} disabled={savingEdit} data-testid="admin-edit-save-btn">
                {savingEdit ? <Loader2 className="animate-spin" size={16} /> : "Enregistrer"}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Messages management dialog */}
      <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
        <DialogContent className="glass border-white/10 sm:max-w-md" data-testid="admin-messages-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium">Messages{msgCandle ? ` — ${msgCandle.deceased_name}` : ""}</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">Modérez les condoléances de cette bougie.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-2 overflow-y-auto pt-1">
            {loadingMsgs ? (
              <div className="flex justify-center py-8"><Loader2 className="animate-spin text-sky-400" /></div>
            ) : msgList.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">Aucun message.</p>
            ) : msgList.map((m) => (
              <div key={m.id} className="flex items-start justify-between gap-3 rounded-2xl border border-white/10 bg-card/30 p-3" data-testid="admin-message-item">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-sky-300">{m.author_name}</p>
                  <p className="mt-0.5 text-sm text-foreground/90">{m.message}</p>
                </div>
                <Button size="sm" variant="ghost" className="h-8 shrink-0 text-destructive" onClick={() => deleteCondolence(m.id)} data-testid="admin-delete-message-btn"><Trash2 size={14} /></Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete with super-admin code dialog */}
      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent className="glass border-white/10 sm:max-w-sm" data-testid="admin-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium">Confirmer la suppression</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Cette action supprime définitivement {delTarget ? delTarget.label : "cet élément"}{delTarget?.type === "user" ? " ainsi que toutes ses bougies, messages et paiements" : ""}{delTarget?.type === "cleanup-empty" ? " (comptes n'ayant allumé aucune bougie), avec leurs notifications et paiements" : ""}. Saisissez le code super admin pour confirmer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <div>
              <Label>Code super admin</Label>
              <PasswordInput value={delCode} onChange={(e) => setDelCode(e.target.value)} placeholder="••••••" data-testid="admin-delete-pin-input" />
            </div>
            <Button variant="destructive" className="w-full rounded-full" onClick={confirmDelete} disabled={deleting} data-testid="admin-delete-confirm-btn">
              {deleting ? <Loader2 className="animate-spin" size={16} /> : "Supprimer définitivement"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={pubOpen} onOpenChange={setPubOpen}>
        <DialogContent className="glass border-white/10 sm:max-w-sm" data-testid="admin-living-publish-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium flex items-center gap-2"><Flame size={18} className="text-amber-300" /> Publier la bougie</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Renseignez la date de décès. Une fois validée, la bougie devient publique et apparaît dans les bougies commémoratives (avec le badge « Aujourd'hui » si le décès est aujourd'hui).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Date de décès</Label>
              <Input type="date" value={pubDeath} onChange={(e) => setPubDeath(e.target.value)} data-testid="admin-living-death-input" />
            </div>
            <Button className="w-full rounded-full bg-amber-500 text-black hover:bg-amber-400" onClick={doPublish} disabled={pubBusy} data-testid="admin-living-publish-confirm-btn">
              {pubBusy ? <Loader2 className="animate-spin" size={16} /> : (pubLc?.published ? "Mettre à jour la date" : "Publier maintenant")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent className="glass max-h-[85vh] overflow-y-auto border-white/10 sm:max-w-2xl" data-testid="admin-duplicates-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium">Doublons détectés</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Mêmes personnes présentes plusieurs fois. Sélectionnez les fiches à supprimer (gardez-en une), à l'unité ou en groupe.
            </DialogDescription>
          </DialogHeader>
          {dupGroups.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="admin-duplicates-empty">Aucun doublon 🎉</div>
          ) : (
            <div className="space-y-4">
              {dupSelected.length > 0 && (
                <Button size="sm" variant="destructive" className="rounded-full" onClick={() => deletePendingIds(dupSelected)} data-testid="admin-duplicates-delete-selected-btn">
                  <Trash2 size={15} className="mr-1.5" /> Supprimer la sélection ({dupSelected.length})
                </Button>
              )}
              {dupGroups.map((g) => (
                <div key={g.key} className="rounded-xl border border-white/10 bg-white/5 p-3" data-testid="admin-duplicate-group">
                  <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
                    <Layers size={15} className="text-amber-300" /> {g.name} <span className="text-xs font-normal text-muted-foreground">— {g.count} fiches</span>
                  </p>
                  <div className="space-y-2">
                    {g.items.map((it) => (
                      <div key={it.id} className="flex items-center gap-3 rounded-lg border border-white/5 bg-white/5 p-2" data-testid="admin-duplicate-item">
                        <Checkbox checked={dupSelected.includes(it.id)} onCheckedChange={() => toggleDupSelect(it.id)} data-testid="admin-duplicate-checkbox" />
                        {it.photo_url ? (
                          <img src={`${process.env.REACT_APP_BACKEND_URL}${it.photo_url}`} alt="" className="h-9 w-9 shrink-0 rounded-full border border-amber-300/40 object-cover" />
                        ) : (
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/10"><Flame size={14} className="text-amber-300/50" /></div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm text-foreground">{it.deceased_name}</p>
                          <p className="text-xs text-muted-foreground">{it.source_label}{it.death_date ? ` · ${fmtDMY(it.death_date)}` : ""}</p>
                        </div>
                        <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-destructive hover:bg-destructive/10" onClick={() => deletePendingIds([it.id])} data-testid="admin-duplicate-delete-one-btn">
                          <Trash2 size={15} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Client sheet dialog */}
      <Dialog open={clientOpen} onOpenChange={setClientOpen}>
        <DialogContent className="glass border-white/10 sm:max-w-lg" data-testid="client-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium">Fiche client</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">Informations éditables et historique en lecture seule.</DialogDescription>
          </DialogHeader>
          {!clientDetail ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-sky-400" /></div>
          ) : (
            <div className="max-h-[70vh] space-y-4 overflow-y-auto pt-1">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Prénom</Label><Input value={cFirst} onChange={(e) => setCFirst(titleCaseName(e.target.value))} data-testid="client-first-name-input" /></div>
                <div><Label>Nom</Label><Input value={cLast} onChange={(e) => setCLast(titleCaseName(e.target.value))} data-testid="client-last-name-input" /></div>
                <div className="col-span-2"><Label>Email</Label><Input value={cEmail} onChange={(e) => setCEmail(e.target.value)} data-testid="client-email-input" /></div>
                <div className="col-span-2">
                  <Label>Portable</Label>
                  <PhoneInput code={cCountry || "FR"} phone={cPhone} onChange={({ code, phone }) => { setCCountry(code); setCPhone(phone); }} testid="client-phone" />
                </div>
                <div className="col-span-2">
                  <Label>Langue</Label>
                  <Select value={cLang} onValueChange={setCLang}>
                    <SelectTrigger data-testid="client-language-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map((l) => (<SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="flex items-center gap-1.5"><KeyRound size={14} className="text-sky-400" /> Nouveau mot de passe</Label>
                  <Input type="text" value={cPassword} onChange={(e) => setCPassword(e.target.value)} placeholder="Laisser vide pour ne pas changer" data-testid="client-password-input" />
                  <p className="mt-1 text-xs text-muted-foreground">Définit ou remplace le mot de passe de connexion du client (min. 6 caractères).</p>
                </div>
              </div>
              <Button className="w-full rounded-full" onClick={saveClient} disabled={savingClient} data-testid="client-save-btn">
                {savingClient ? <Loader2 className="animate-spin" size={16} /> : "Enregistrer la fiche"}
              </Button>

              {isAdmin && clientDetail.client.role !== "admin" && (
                <div className="rounded-2xl border border-fuchsia-400/25 bg-fuchsia-400/[0.04] p-4" data-testid="staff-access-section">
                  <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-fuchsia-200"><ShieldCheck size={15} /> Accès staff (rôle & onglets)</div>
                  <p className="mb-3 text-xs text-muted-foreground">
                    Cochez les onglets Admin auxquels ce compte peut accéder. Les identifiants de connexion de ce client détermineront alors ses accès. Aucun onglet coché = simple client. Modification protégée par le code super admin.
                  </p>
                  <div className="mb-3 flex items-center gap-2 text-xs">
                    <span className="text-muted-foreground">Rôle actuel :</span>
                    <span className={`rounded-full px-2 py-0.5 font-medium ${clientDetail.client.role === "staff" ? "bg-fuchsia-500/20 text-fuchsia-200" : "bg-white/10 text-muted-foreground"}`} data-testid="staff-current-role">
                      {clientDetail.client.role === "staff" ? "Staff" : "Client"}
                    </span>
                  </div>
                  <div className="mb-3 grid grid-cols-2 gap-2">
                    {DEFAULT_TAB_ORDER.map((t) => (
                      <label key={t} className="flex cursor-pointer items-center gap-2 rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm" data-testid={`staff-tab-check-${t}`}>
                        <Checkbox checked={staffTabs.includes(t)} onCheckedChange={() => toggleStaffTab(t)} />
                        {TAB_DEFS[t].label}
                      </label>
                    ))}
                  </div>
                  <Label>Code super admin</Label>
                  <Input type="password" value={staffCode} onChange={(e) => setStaffCode(e.target.value)} placeholder="Code super admin" data-testid="staff-super-code-input" />
                  <Button className="mt-3 w-full rounded-full" onClick={saveStaff} disabled={savingStaff} data-testid="staff-save-btn">
                    {savingStaff ? <Loader2 className="animate-spin" size={16} /> : "Enregistrer les accès staff"}
                  </Button>
                </div>
              )}

              <div className="border-t border-white/10 pt-3">
                <p className="mb-2 text-sm font-medium">Bougies ({clientDetail.candles.length})</p>
                <div className="space-y-1">
                  {clientDetail.candles.map((c) => (
                    <div key={c.id} className="flex justify-between rounded-xl border border-white/10 bg-card/30 px-3 py-2 text-sm" data-testid="client-candle-item">
                      <span>{c.deceased_name}</span>
                      <span className="text-muted-foreground">{c.eternal ? "Éternelle" : (c.paid ? (c.is_expired ? "Éteinte" : "Active") : "Impayée")} · {euros(c.total_paid_cents)}</span>
                    </div>
                  ))}
                  {clientDetail.candles.length === 0 && <p className="text-xs text-muted-foreground">Aucune bougie.</p>}
                </div>
              </div>
              <div>
                <p className="mb-2 text-sm font-medium">Paiements ({clientDetail.payments.length})</p>
                <div className="space-y-1">
                  {clientDetail.payments.map((p) => (
                    <div key={p.id} className="flex justify-between rounded-xl border border-white/10 bg-card/30 px-3 py-2 text-sm" data-testid="client-payment-item">
                      <span>{p.type === "light" ? "Allumage" : "Ravivage"} · {p.mode}</span>
                      <span className="text-muted-foreground">{euros(p.amount)}</span>
                    </div>
                  ))}
                  {clientDetail.payments.length === 0 && <p className="text-xs text-muted-foreground">Aucun paiement.</p>}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Nouveau client */}
      <Dialog open={newClientOpen} onOpenChange={setNewClientOpen}>
        <DialogContent className="glass border-white/10 sm:max-w-lg" data-testid="new-client-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium">Nouveau client</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">Créez une fiche client. Le mot de passe est optionnel (requis uniquement pour permettre la connexion par email).</DialogDescription>
          </DialogHeader>
          <div className="max-h-[70vh] space-y-4 overflow-y-auto pt-1">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Prénom</Label><Input value={ncFirst} onChange={(e) => setNcFirst(titleCaseName(e.target.value))} data-testid="new-client-first-name-input" /></div>
              <div><Label>Nom</Label><Input value={ncLast} onChange={(e) => setNcLast(titleCaseName(e.target.value))} data-testid="new-client-last-name-input" /></div>
              <div className="col-span-2"><Label>Email *</Label><Input type="email" value={ncEmail} onChange={(e) => setNcEmail(e.target.value)} data-testid="new-client-email-input" /></div>
              <div className="col-span-2">
                <Label>Portable</Label>
                <PhoneInput code={ncCountry || "FR"} phone={ncPhone} onChange={({ code, phone }) => { setNcCountry(code); setNcPhone(phone); }} testid="new-client-phone" />
              </div>
              <div className="col-span-2">
                <Label>Langue</Label>
                <Select value={ncLang} onValueChange={setNcLang}>
                  <SelectTrigger data-testid="new-client-language-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map((l) => (<SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Mot de passe (optionnel)</Label><Input type="text" value={ncPassword} onChange={(e) => setNcPassword(e.target.value)} placeholder="Laisser vide = pas de connexion par email" data-testid="new-client-password-input" /></div>
            </div>
            <Button className="w-full rounded-full" onClick={createClient} disabled={creatingClient} data-testid="new-client-save-btn">
              {creatingClient ? <Loader2 className="animate-spin" size={16} /> : "Créer le client"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Nouvelle bougie (admin -> client) */}
      <Dialog open={newCandleOpen} onOpenChange={setNewCandleOpen}>
        <DialogContent className="glass border-white/10 sm:max-w-md" data-testid="new-candle-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium">Nouvelle bougie</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">Créez une bougie rattachée à un client. Elle sera allumée immédiatement pour la durée choisie.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[70vh] space-y-4 overflow-y-auto pt-1">
            <div>
              <Label>Client *</Label>
              <Select value={nkOwner} onValueChange={setNkOwner}>
                <SelectTrigger data-testid="new-candle-owner-select"><SelectValue placeholder="Choisir un client" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {allClientsFlat.filter((cl) => cl.email).map((cl) => (
                    <SelectItem key={cl.id} value={cl.id}>{flagEmoji(cl.country)} {cl.name || cl.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Photo</Label>
              {nkPhoto ? (
                <div className="relative mt-1 w-fit">
                  <img src={nkPhoto} alt="aperçu" className="h-24 w-24 rounded-full border-2 border-amber-300/80 object-cover shadow-[0_0_20px_rgba(251,191,36,0.3)]" />
                  <button type="button" onClick={() => setNkPhoto(null)} className="absolute -right-2 -top-2 rounded-full bg-destructive p-1" data-testid="new-candle-remove-photo-btn"><X size={14} /></button>
                </div>
              ) : (
                <button type="button" onClick={() => newCandleFileRef.current?.click()} className="mt-1 flex h-24 w-24 flex-col items-center justify-center rounded-full border-2 border-dashed border-amber-300/40 text-muted-foreground transition-colors hover:border-amber-300 hover:text-amber-300" data-testid="new-candle-add-photo-btn">
                  <ImagePlus size={20} />
                  <span className="mt-1 text-xs">Ajouter</span>
                </button>
              )}
              <input ref={newCandleFileRef} type="file" accept="image/*" className="hidden" onChange={onNewCandlePhoto} />
            </div>
            <div>
              <Label>Nom du défunt *</Label>
              <Input value={nkName} onChange={(e) => setNkName(titleCaseName(e.target.value))} data-testid="new-candle-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Naissance</Label><Input type="date" value={nkBirth} onChange={(e) => setNkBirth(e.target.value)} data-testid="new-candle-birth-date" /></div>
              <div><Label>Décès</Label><Input type="date" value={nkDeath} onChange={(e) => setNkDeath(e.target.value)} data-testid="new-candle-death-date" /></div>
            </div>
            <div>
              <Label>Nationalité (facultatif — drapeau)</Label>
              <select value={nkNat} onChange={(e) => setNkNat(e.target.value)} data-testid="new-candle-nationality" className="h-10 w-full rounded-md border border-white/15 bg-black/40 px-3 text-sm text-white outline-none focus:border-amber-300">
                <option value="">— Aucune —</option>
                {NATIONALITIES.map((n) => (<option key={n.value} value={n.value} className="bg-neutral-900">{n.label}</option>))}
              </select>
            </div>
            <div>
              <Label>Message d'hommage</Label>
              <Textarea rows={4} value={nkMsg} onChange={(e) => setNkMsg(e.target.value)} data-testid="new-candle-message-input" />
            </div>
            <div>
              <Label>Durée</Label>
              <Select value={nkPlan} onValueChange={setNkPlan}>
                <SelectTrigger data-testid="new-candle-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CANDLE_PLANS.map((p) => (<SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <Button className="w-full rounded-full" onClick={createCandle} disabled={creatingCandle} data-testid="new-candle-save-btn">
              {creatingCandle ? <Loader2 className="animate-spin" size={16} /> : "Créer et allumer la bougie"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Détail complet de la bougie */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="glass border-white/10 sm:max-w-md" data-testid="admin-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium">Détail de la bougie</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">Toutes les informations en entier.</DialogDescription>
          </DialogHeader>
          {detailCandle && (
            <div className="max-h-[72vh] space-y-4 overflow-y-auto pt-1">
              <div className="flex flex-col items-center text-center">
                <div className={`inline-block rounded-full ${ringStyle(detailCandle.flag_colors) ? "p-[3px]" : ""}`} style={ringStyle(detailCandle.flag_colors) || undefined} title={detailCandle.country_name || ""}>
                  <img src={detailCandle.photo_base64 || "/candle.png"} alt={detailCandle.deceased_name} className={`h-28 w-28 rounded-full object-cover ${ringStyle(detailCandle.flag_colors) ? "border-2 border-black/60" : "border-2 border-amber-300/80 shadow-[0_0_20px_rgba(251,191,36,0.3)]"}`} data-testid="detail-photo" />
                </div>
                <p className="mt-3 text-lg font-medium" data-testid="detail-name">{detailCandle.deceased_name}</p>
              </div>
              {detailCandle.tribute_message ? (
                <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                  <p className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">Message d'hommage</p>
                  <p className="whitespace-pre-wrap break-words text-sm italic text-foreground/90" data-testid="detail-tribute">{detailCandle.tribute_message}</p>
                </div>
              ) : (
                <p className="text-center text-xs text-muted-foreground">Aucun message d'hommage.</p>
              )}
              <div className="space-y-2 rounded-2xl border border-white/10 bg-card/30 p-4 text-sm">
                <div className="flex justify-between gap-3"><span className="text-muted-foreground">Client</span><span className="text-right font-medium">{detailCandle.owner_name || "—"}</span></div>
                {(detailCandle.birth_date || detailCandle.death_date) && (
                  <div className="flex justify-between gap-3" data-testid="detail-dates"><span className="text-muted-foreground">Naissance / Décès</span><span className="text-right">{fmtDate(detailCandle.birth_date)} — {fmtDate(detailCandle.death_date)}</span></div>
                )}
                <div className="flex justify-between gap-3"><span className="text-muted-foreground">État</span><span className="text-right">{detailCandle.eternal ? "Éternelle" : (detailCandle.paid ? (detailCandle.is_expired ? "Éteinte" : `Active · ${Math.round(detailCandle.remaining_seconds / 3600)}h restantes`) : "Impayée")}</span></div>
                <div className="flex justify-between gap-3"><span className="text-muted-foreground">Forfait</span><span className="text-right">{detailCandle.plan || "—"}</span></div>
                <div className="flex justify-between gap-3"><span className="text-muted-foreground">Nombre de messages</span><span className="text-right">{detailCandle.condolence_count || 0}</span></div>
                {detailCandle.lit_at && <div className="flex justify-between gap-3"><span className="text-muted-foreground">Allumée le</span><span className="text-right">{new Date(detailCandle.lit_at).toLocaleString("fr-FR")}</span></div>}
                {detailCandle.expires_at && <div className="flex justify-between gap-3"><span className="text-muted-foreground">Expire le</span><span className="text-right">{new Date(detailCandle.expires_at).toLocaleString("fr-FR")}</span></div>}
              </div>
              {detailCandle.share_slug && (
                <a href={`/candle/${detailCandle.id}`} target="_blank" rel="noreferrer" className="block w-full rounded-full border border-white/15 py-2.5 text-center text-sm text-sky-400 transition-colors hover:bg-white/5" data-testid="detail-open-memorial">Ouvrir le mémorial</a>
              )}

              <div className="rounded-2xl border border-white/10 bg-black/20 p-4" data-testid="detail-broadcast">
                <p className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">Diffusion (broadcasting)</p>
                <p className="mb-3 text-[11px] text-muted-foreground">Réseaux sur lesquels diffuser cette flamme (préparation — envoi à venir).</p>
                {activeNets.length === 0 ? (
                  <p className="text-xs text-amber-300/80">Aucun réseau actif. Connectez des réseaux dans « Connexions réseaux ».</p>
                ) : (
                  <div className="space-y-2">
                    {activeNets.map((net) => {
                      const checked = (detailCandle.broadcast_networks || []).includes(net);
                      return (
                        <label key={net} className="flex cursor-pointer items-center justify-between rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm hover:bg-white/[0.06]" data-testid={`broadcast-row-${net}`}>
                          <span className="font-medium">{NET_LABELS[net] || net}</span>
                          <input type="checkbox" checked={checked} onChange={() => toggleBroadcast(net)} className="h-4 w-4 accent-sky-500" data-testid={`broadcast-check-${net}`} />
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
